import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import DropdownMenu from './DropdownMenu';
import BookDropdown from './BookDropdown';
import BookPageFields, { EMPTY_BOOK_PAGE, bookPagePayload, describeSavedBookPage } from './BookPageFields';
import InspirationDropdown, { getInspirationKey, parseInspirationValue } from './InspirationDropdown';
import RichTextEditor, { isRichTextEmpty, sanitizeRichTextHtml } from './RichTextEditor';
import MultiSelectDropdown from './MultiSelectDropdown';
import TagSelect from './TagSelect';
import { coloredPencilSetsAPI, coloredPencilsAPI, brandsAPI, inspirationAPI, playlistsAPI, videosAPI, colorPalettesAPI, colorCombosAPI, journalEntriesAPI, apiGet } from '../services/api';
import { deltaEToPercentage } from '../utils/colorUtils';
import { extractYouTubeVideoId } from '../utils/youtubeUtils';
import { buildColorAlongVideoPath, formatVideoTimeRange } from '../utils/colorAlongUtils';
import { formatEntryProjectDates, formatSessionNoteStamp, getLocalDateString } from '../utils/dateUtils';
import YouTubeImportBanner from './YouTubeImportBanner';
import YouTubeImportPlaceholder from './YouTubeImportPlaceholder';
import { useYouTubeImport, useYouTubeImportRefresh } from '../context/YouTubeImportContext';
import VideoComments, { areCommentsLocked } from './VideoComments';

const SHOW_COMMENTS_KEY = 'color_along_show_comments';

// Color distance calculation using Euclidean distance in RGB space
const colorDistance = (color1, color2) => {
  const r1 = parseInt(color1.slice(1, 3), 16);
  const g1 = parseInt(color1.slice(3, 5), 16);
  const b1 = parseInt(color1.slice(5, 7), 16);
  
  const r2 = parseInt(color2.slice(1, 3), 16);
  const g2 = parseInt(color2.slice(3, 5), 16);
  const b2 = parseInt(color2.slice(5, 7), 16);
  
  return Math.sqrt(
    Math.pow(r2 - r1, 2) + 
    Math.pow(g2 - g1, 2) + 
    Math.pow(b2 - b1, 2)
  );
};

// Helper function to ensure hex values have # prefix
const normalizeHex = (hex) => {
  if (!hex) return '#000000';
  if (typeof hex !== 'string') return '#000000';
  return hex.startsWith('#') ? hex : `#${hex}`;
};

const getThumbnailUrl = (thumb) => {
  if (!thumb) return null;
  if (thumb.startsWith('http')) return thumb;
  const apiBase = process.env.REACT_APP_API_BASE_URL?.replace('/api', '') || 'http://localhost:8000';
  return `${apiBase}/storage/${thumb}`;
};

const LIST_THUMB_ICONS = {
  brand: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
  set: 'M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z',
};

const ListThumb = ({ src, alt, icon, className = 'w-7 h-7' }) => {
  const [failed, setFailed] = useState(false);
  const url = getThumbnailUrl(src);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  if (url && !failed) {
    return (
      <img
        src={url}
        alt={alt}
        className={`${className} rounded object-cover flex-shrink-0 bg-white border border-slate-200`}
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <div className={`${className} rounded flex items-center justify-center flex-shrink-0 bg-slate-100`}>
      <svg className="w-1/2 h-1/2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={LIST_THUMB_ICONS[icon]} />
      </svg>
    </div>
  );
};

const buildSetDropdownOptions = (setSizes = []) => {
  const grouped = new Map();

  setSizes.forEach((setSize) => {
    const setId = setSize.set?.id || setSize.id;
    if (!setId) return;
    const existing = grouped.get(setId) || {
      setId,
      name: setSize.set?.name || setSize.name || 'Unknown',
      brand: typeof setSize.set?.brand === 'object'
        ? setSize.set?.brand?.name
        : (setSize.set?.brand || setSize.brand || 'Unknown'),
      totalColors: 0,
      thumbs: []
    };

    existing.totalColors += (setSize.count || 0);
    const thumbUrl = getThumbnailUrl(setSize.thumb || setSize.set?.thumb);
    if (thumbUrl && !existing.thumbs.includes(thumbUrl)) {
      existing.thumbs.push(thumbUrl);
    }
    grouped.set(setId, existing);
  });

  return Array.from(grouped.values())
    .sort((a, b) => `${a.brand} ${a.name}`.localeCompare(`${b.brand} ${b.name}`))
    .map((option) => ({
      value: option.setId.toString(),
      label: `${option.name} (${option.brand})`,
      selectedLabel: `${option.name} (${option.brand})`,
      displayLabel: (
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 min-w-[2.75rem]">
            {option.thumbs.slice(0, 3).map((thumb) => (
              <img
                key={`${option.setId}-${thumb}`}
                src={thumb}
                alt={`${option.name} size`}
                className="w-7 h-7 rounded object-cover border border-slate-200"
              />
            ))}
            {option.thumbs.length === 0 && (
              <div className="w-7 h-7 rounded bg-slate-100 border border-slate-200" />
            )}
          </div>
          <div className="min-w-0">
            <div className="text-sm text-slate-900 font-medium truncate">{option.name}</div>
            <div className="text-xs text-slate-500 truncate">
              {option.brand} - {option.totalColors} colors
            </div>
          </div>
        </div>
      )
    }));
};

// Get the first character (number or letter) from a color name for indexing
const getIndexChar = (colorName) => {
  if (!colorName || typeof colorName !== 'string') return '#';
  const firstChar = colorName.trim().charAt(0).toUpperCase();
  // If it's a number, return it; if it's a letter, return it; otherwise return '#'
  if (/[0-9]/.test(firstChar)) return firstChar;
  if (/[A-Z]/.test(firstChar)) return firstChar;
  return '#';
};

// Find closest matching color
const findClosestColor = (sourceColor, targetSet) => {
  let closest = null;
  let minDistance = Infinity;
  
  targetSet.colors.forEach((targetColor) => {
    const distance = colorDistance(sourceColor.hex, targetColor.hex);
    if (distance < minDistance) {
      minDistance = distance;
      closest = { ...targetColor, distance };
    }
  });
  
  return closest;
};

const JOURNAL_DRAFT_STORAGE_KEY = 'color_along_journal_draft';

// A journal draft belongs to the selected video (or image) and is discarded when the selection changes
const getJournalDraftInspirationKey = (video, image) => {
  if (video?.id) return `video:${video.id}`;
  if (image?.id) return `image:${image.id}`;
  return null;
};

const readJournalDraft = () => {
  try {
    const saved = localStorage.getItem(JOURNAL_DRAFT_STORAGE_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch (error) {
    console.error('Error loading journal draft:', error);
    return null;
  }
};

const writeJournalDraft = (draft) => {
  try {
    localStorage.setItem(JOURNAL_DRAFT_STORAGE_KEY, JSON.stringify(draft));
  } catch (error) {
    console.error('Error saving journal draft:', error);
  }
};

const clearJournalDraft = () => {
  try {
    localStorage.removeItem(JOURNAL_DRAFT_STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing journal draft:', error);
  }
};

const ColorAlong = ({ user, onInspirationClick }) => {
  const { importing } = useYouTubeImport();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bookParam = searchParams.get('book');
  const urlBookId = bookParam && /^\d+$/.test(bookParam) ? bookParam : '';
  const [videoId, setVideoId] = useState('');
  const [videoLoadError, setVideoLoadError] = useState(null);
  const [loadingVideo, setLoadingVideo] = useState(false);
  const [videoSetId, setVideoSetId] = useState(null);
  const [userSetId, setUserSetId] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showComments, setShowComments] = useState(() => localStorage.getItem(SHOW_COMMENTS_KEY) === '1');
  const journalInspirationKey = useMemo(
    () => getJournalDraftInspirationKey(selectedVideo, selectedImage),
    [selectedVideo, selectedImage]
  );
  
  // API data states
  const [allPencilSets, setAllPencilSets] = useState([]);
  const [userPencilSetSizes, setUserPencilSetSizes] = useState([]); // Store full set size data
  const [userPencilSets, setUserPencilSets] = useState([]); // Transformed for dropdown
  const [videoSet, setVideoSet] = useState(null);
  const [userSet, setUserSet] = useState(null);
  const [loadingSets, setLoadingSets] = useState(true);
  const [loadingColors, setLoadingColors] = useState(false);
  const [includeTwoColorMix, setIncludeTwoColorMix] = useState(false);
  const [matches, setMatches] = useState([]);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [showMatchesMenu, setShowMatchesMenu] = useState(false);
  const [isMatchesCollapsed, setIsMatchesCollapsed] = useState(false);
  const [groupedMatches, setGroupedMatches] = useState({});
  const [rowMixMatches, setRowMixMatches] = useState({}); // Store per-row two-color mix matches: { videoColorId: match }
  const [loadingRowMix, setLoadingRowMix] = useState({}); // Track loading state per row: { videoColorId: boolean }
  const [markingOut, setMarkingOut] = useState({}); // Track which pencils are being marked as out: { pencilId: boolean }
  const matchesScrollRef = useRef(null);
  const matchesScrollPositionRef = useRef(0);

  // Video Set Selection State (2-step: brand -> set)
  const [videoStep, setVideoStep] = useState('brand'); // 'brand', 'set'
  const [brands, setBrands] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [videoSelectedBrand, setVideoSelectedBrand] = useState(null);
  const [videoSetsForBrand, setVideoSetsForBrand] = useState([]);
  const [loadingVideoSets, setLoadingVideoSets] = useState(false);
  const [videoSelectedSet, setVideoSelectedSet] = useState(null);
  const [pendingVideoSetId, setPendingVideoSetId] = useState(null);
  const videoSetSizesCacheRef = useRef(new Map());
  const [videoSelectedSetSize, setVideoSelectedSetSize] = useState(null); // Store selected set size for display
  const videoSelectedSetSizeRef = useRef(null);
  videoSelectedSetSizeRef.current = videoSelectedSetSize;
  const [videoIncludedSetSizeIds, setVideoIncludedSetSizeIds] = useState([]);

  // User Set Selection State (dropdown, same as journal)
  const [, setUserSelectedSetSize] = useState(null);

  // Scroll to a section by index character
  const scrollToSection = (char) => {
    const element = document.getElementById(`color-section-${char}`);
    if (element) {
      // Get the scrollable container (the matches content div)
      const scrollContainer = element.closest('.overflow-y-auto');
      if (scrollContainer) {
        const containerTop = scrollContainer.getBoundingClientRect().top;
        const elementTop = element.getBoundingClientRect().top;
        const offset = elementTop - containerTop - 8; // 8px padding
        scrollContainer.scrollBy({ top: offset, behavior: 'smooth' });
      } else {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  // Inspiration data state
  const [inspirations, setInspirations] = useState([]);
  const currentInspiration = useMemo(() => {
    if (selectedVideo) {
      return inspirations.find((insp) => (
        insp.type === 'video'
        && (insp.embed_id === selectedVideo.id || insp.embed_id === selectedVideo.embed_id || insp.id === selectedVideo.id)
      )) || null;
    }
    if (selectedImage) {
      return inspirations.find((insp) => (
        (insp.type === 'file' || insp.type === 'image' || insp.type === 'pdf')
        && (insp.id === selectedImage.id || insp.id === parseInt(selectedImage.id, 10))
      )) || null;
    }
    return null;
  }, [inspirations, selectedVideo, selectedImage]);
  const [loadingInspirations, setLoadingInspirations] = useState(true);
  const [pickerVideos, setPickerVideos] = useState([]);
  const [loadingPickerVideos, setLoadingPickerVideos] = useState(true);
  const [playlists, setPlaylists] = useState([]);
  const [loadVideoPlaylistId, setLoadVideoPlaylistId] = useState('');
  const [loadingPlaylists, setLoadingPlaylists] = useState(true);
  const [librarySearch, setLibrarySearch] = useState('');
  const [debouncedLibrarySearch, setDebouncedLibrarySearch] = useState('');
  const [activePlaylist, setActivePlaylist] = useState(null);
  const [playlistVideos, setPlaylistVideos] = useState([]);
  const [loadingPlaylistVideos, setLoadingPlaylistVideos] = useState(false);

  // Journal entry modal states
  const [showJournalModal, setShowJournalModal] = useState(false);
  const [journalMinimized, setJournalMinimized] = useState(false);
  const journalDraftInitializedRef = useRef(false);
  // Inspiration key the open draft belongs to, and the last selected inspiration key
  const journalDraftKeyRef = useRef(null);
  const previousJournalInspirationKeyRef = useRef(null);
  const [palettes, setPalettes] = useState([]);
  const [combos, setCombos] = useState([]);
  const [journalFormData, setJournalFormData] = useState({
    date: getLocalDateString(),
    inspiration: '',
    videoPencilSet: '',
    userPencilSet: '',
    book: '',
    bookPage: EMPTY_BOOK_PAGE,
    palette: '',
    combos: [],
    tags: [],
    notes: ''
  });
  const [loadingJournalData, setLoadingJournalData] = useState(false);
  const [savingJournal, setSavingJournal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [journalVideoStep, setJournalVideoStep] = useState('brand');
  const [journalVideoSelectedBrand, setJournalVideoSelectedBrand] = useState(null);
  const [journalVideoSetsForBrand, setJournalVideoSetsForBrand] = useState([]);
  const [loadingJournalVideoSets, setLoadingJournalVideoSets] = useState(false);
  const [journalVideoSelectedSet, setJournalVideoSelectedSet] = useState(null);
  const [existingJournalEntry, setExistingJournalEntry] = useState(null);
  const [journalVideoStartSeconds, setJournalVideoStartSeconds] = useState(null);

  // YouTube progress tracking
  const youtubePlayerRef = useRef(null);
  const youtubeApiReadyRef = useRef(false);
  const progressSaveIntervalRef = useRef(null);
  const [youtubeApiLoaded, setYoutubeApiLoaded] = useState(false);

  const toggleComments = useCallback(() => {
    setShowComments((open) => {
      localStorage.setItem(SHOW_COMMENTS_KEY, open ? '0' : '1');
      return !open;
    });
  }, []);

  const seekVideo = useCallback((seconds) => {
    const player = youtubePlayerRef.current;
    if (!player?.seekTo) return;
    player.seekTo(seconds, true);
    player.playVideo?.();
  }, []);

  const getYouTubeTimeSeconds = useCallback(() => {
    try {
      const currentTime = youtubePlayerRef.current?.getCurrentTime?.();
      if (typeof currentTime === 'number' && Number.isFinite(currentTime) && currentTime >= 0) {
        return Math.floor(currentTime);
      }
    } catch (error) {
      console.error('Error reading video time:', error);
    }
    return null;
  }, []);

  // Helper functions for video progress
  const getVideoProgressKey = (videoId) => `youtube_progress_${videoId}`;
  
  const saveVideoProgress = (videoId, currentTime) => {
    if (!videoId || !currentTime) return;
    try {
      const progressData = {
        currentTime: Math.floor(currentTime),
        timestamp: Date.now()
      };
      localStorage.setItem(getVideoProgressKey(videoId), JSON.stringify(progressData));
    } catch (error) {
      console.error('Error saving video progress:', error);
    }
  };

  const getVideoProgress = (videoId) => {
    if (!videoId) return null;
    try {
      const saved = localStorage.getItem(getVideoProgressKey(videoId));
      if (saved) {
        const progressData = JSON.parse(saved);
        // Only use progress if it's less than 24 hours old
        const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);
        if (progressData.timestamp > oneDayAgo && progressData.currentTime > 5) {
          return progressData.currentTime;
        }
      }
    } catch (error) {
      console.error('Error loading video progress:', error);
    }
    return null;
  };

  // Get video from location state if navigating from Library
  useEffect(() => {
    if (location.state?.video) {
      setSelectedVideo(location.state.video);
      setVideoId(location.state.video.id || '');
      // Close the left navigation when video is loaded from location state
      if (onInspirationClick) {
        onInspirationClick();
      }
    }
  }, [location.state, onInspirationClick]);

  // Get video/image from query string parameters
  useEffect(() => {
    const videoParam = searchParams.get('video');
    const imageParam = searchParams.get('image');
    
    if (videoParam) {
      const resolvedVideoId = extractYouTubeVideoId(videoParam) || videoParam;
      // Load video from query parameter
      // Try to find it in loaded inspirations first
      const videoInspiration = inspirations.find(insp => 
        insp.type === 'video' && (insp.embed_id === resolvedVideoId || insp.embed_id === videoParam || insp.id === parseInt(videoParam))
      );
      
      if (videoInspiration) {
        setSelectedVideo({
          id: videoInspiration.embed_id,
          embed_id: videoInspiration.embed_id,
          title: videoInspiration.title || 'Video'
        });
        setVideoId(videoInspiration.embed_id);
      } else {
        setVideoId(resolvedVideoId);
        setSelectedVideo({ id: resolvedVideoId, title: 'Video' });
      }
      // Close the left navigation when video is loaded from URL
      if (onInspirationClick) {
        onInspirationClick();
      }
    } else if (imageParam) {
      // Load image from query parameter
      // Try to find it in loaded inspirations first
      const imageInspiration = inspirations.find(insp => 
        insp.type === 'file' && insp.id === parseInt(imageParam)
      );
      
      if (imageInspiration) {
        setSelectedImage({
          id: imageInspiration.id,
          title: imageInspiration.title || 'Image',
          thumbnail: imageInspiration.thumbnail_path || imageInspiration.path,
          path: imageInspiration.path
        });
      } else {
        setSelectedImage({ id: imageParam });
      }
      // Close the left navigation when image is loaded from URL
      if (onInspirationClick) {
        onInspirationClick();
      }
    }
  }, [searchParams, inspirations, onInspirationClick]);

  // Get pencil set from query string parameter - sets the video set (system set), never the user's set
  const pencilSetParam = searchParams.get('pencilSet');
  useEffect(() => {
    if (!pencilSetParam || !/^\d+$/.test(pencilSetParam)) {
      return undefined;
    }

    // The pencilSetParam should be a set size ID
    const pencilSetSizeId = parseInt(pencilSetParam, 10);
    let cancelled = false;

    coloredPencilSetsAPI.getAvailableSetSizes(1, 1, true, { excludePencils: true, id: pencilSetSizeId })
      .then((response) => {
        const setSize = (Array.isArray(response) ? response : response?.data || [])[0];
        // Get the set ID from the set size (this is the parent set)
        const setId = setSize?.set?.id || setSize?.colored_pencil_set_id;
        if (cancelled || !setId) return;

        // Set the video set ID (for "Video Set")
        setVideoSetId(setId);

        // Set the selected video set size for display
        setVideoSelectedSetSize(setSize);

        // Reset the video step selection UI
        setVideoStep('brand');
        setVideoSelectedBrand(null);
        setVideoSelectedSet(null);
        setVideoSetsForBrand([]);
      })
      .catch((error) => console.error('Error loading pencil set from URL:', error));

    return () => {
      cancelled = true;
    };
  }, [pencilSetParam]);

  // Fetch brands on mount (for video set selection - system sets)
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        setLoadingBrands(true);
        const response = await brandsAPI.getAll(1, 100);
        let brandsData = [];
        if (Array.isArray(response)) {
          brandsData = response;
        } else if (response.data && Array.isArray(response.data)) {
          brandsData = response.data;
        }
        setBrands(brandsData);
      } catch (err) {
        console.error('Error fetching brands:', err);
      } finally {
        setLoadingBrands(false);
      }
    };

    fetchBrands();
  }, []);

  // Load YouTube IFrame API
  useEffect(() => {
    // Check if API is already loaded
    if (window.YT && window.YT.Player) {
      youtubeApiReadyRef.current = true;
      setYoutubeApiLoaded(true);
      return;
    }

    // Set up callback first (before script loads)
    const originalCallback = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      youtubeApiReadyRef.current = true;
      setYoutubeApiLoaded(true);
      // Call original callback if it exists
      if (originalCallback && typeof originalCallback === 'function') {
        originalCallback();
      }
    };

    // Check if script is already being loaded
    if (document.querySelector('script[src*="youtube.com/iframe_api"]')) {
      // Wait for it to load
      const checkInterval = setInterval(() => {
        if (window.YT && window.YT.Player) {
          youtubeApiReadyRef.current = true;
          setYoutubeApiLoaded(true);
          clearInterval(checkInterval);
        }
      }, 100);
      return () => clearInterval(checkInterval);
    }

    // Load the script
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    tag.async = true;
    const firstScriptTag = document.getElementsByTagName('script')[0];
    if (firstScriptTag && firstScriptTag.parentNode) {
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    } else {
      document.head.appendChild(tag);
    }
  }, []);

  // Initialize YouTube player when video is selected
  useEffect(() => {
    if (!selectedVideo || !selectedVideo.id) {
      return;
    }

    // Clean up previous player
    if (youtubePlayerRef.current) {
      try {
        youtubePlayerRef.current.destroy();
      } catch (error) {
        // Player might already be destroyed
      }
      youtubePlayerRef.current = null;
    }

    // Clear any existing progress save interval
    if (progressSaveIntervalRef.current) {
      clearInterval(progressSaveIntervalRef.current);
      progressSaveIntervalRef.current = null;
    }

    // If API is not ready, use fallback iframe (handled in render)
    if (!youtubeApiReadyRef.current) {
      return;
    }

    // Get saved progress
    const savedProgress = getVideoProgress(selectedVideo.id);
    
    // Wait a bit for the container to be rendered
    const initPlayer = () => {
      const playerContainer = document.getElementById('youtube-player-container');
      if (!playerContainer) {
        // Retry after a short delay
        setTimeout(initPlayer, 100);
        return;
      }

      // Clear container
      playerContainer.innerHTML = '';

      // Create a div for the player
      const playerId = `youtube-player-${selectedVideo.id}-${Date.now()}`;
      const playerDiv = document.createElement('div');
      playerDiv.id = playerId;
      playerDiv.style.width = '100%';
      playerDiv.style.height = '100%';
      playerDiv.style.display = 'block';
      playerContainer.appendChild(playerDiv);

      try {
        const player = new window.YT.Player(playerId, {
          videoId: selectedVideo.id,
          width: '100%',
          height: '100%',
          playerVars: {
            autoplay: 0,
            controls: 1,
            rel: 0,
            modestbranding: 1,
            ...(savedProgress ? { start: savedProgress } : {})
          },
          events: {
            onReady: (event) => {
              youtubePlayerRef.current = event.target;
              // Start tracking progress
              progressSaveIntervalRef.current = setInterval(() => {
                if (youtubePlayerRef.current) {
                  try {
                    const currentTime = youtubePlayerRef.current.getCurrentTime();
                    if (currentTime && currentTime > 0) {
                      saveVideoProgress(selectedVideo.id, currentTime);
                    }
                  } catch (error) {
                    console.error('Error getting video progress:', error);
                  }
                }
              }, 5000); // Save every 5 seconds
            },
            onStateChange: (event) => {
              // Save progress when video is paused or ended
              if (event.data === window.YT.PlayerState.PAUSED || event.data === window.YT.PlayerState.ENDED) {
                if (youtubePlayerRef.current) {
                  try {
                    const currentTime = youtubePlayerRef.current.getCurrentTime();
                    if (currentTime && currentTime > 0) {
                      saveVideoProgress(selectedVideo.id, currentTime);
                    }
                  } catch (error) {
                    console.error('Error saving video progress:', error);
                  }
                }
              }
            }
          }
        });
      } catch (error) {
        console.error('Error creating YouTube player:', error);
      }
    };

    // Small delay to ensure DOM is ready
    setTimeout(initPlayer, 100);

    // Cleanup function
    return () => {
      if (progressSaveIntervalRef.current) {
        clearInterval(progressSaveIntervalRef.current);
        progressSaveIntervalRef.current = null;
      }
      if (youtubePlayerRef.current) {
        try {
          youtubePlayerRef.current.destroy();
        } catch (error) {
          // Player might already be destroyed
        }
        youtubePlayerRef.current = null;
      }
    };
  }, [selectedVideo, youtubeApiLoaded]);

  // Fetch user's pencil sets for Your Set dropdown (keep for journal entry)
  useEffect(() => {
    const fetchUserPencilSets = async () => {
      try {
        const response = await coloredPencilSetsAPI.getAll(1, 100, true, {
          sort: 'favorites_first,brand,set_name,count',
          archived: false
        });
        
        // Handle paginated response
        let setsData = [];
        if (Array.isArray(response)) {
          setsData = response;
        } else if (response.data && Array.isArray(response.data)) {
          setsData = response.data;
        }
        
        // Store full set size data
        setUserPencilSetSizes(setsData);
        
        // Transform ColoredPencilSetSizeResource to match expected format for dropdown
        const transformedSets = setsData.map(setSize => ({
          id: setSize.set?.id || setSize.id, // Use set ID for selection
          name: setSize.set?.name || 'Unknown',
          brand: setSize.set?.brand || 'Unknown',
          count: setSize.count || 0,
          setSizeId: setSize.id, // Keep the set size ID
          setSizeData: setSize // Keep full data for color fetching
        }));
        
        setUserPencilSets(transformedSets);
      } catch (error) {
        console.error('Error fetching user pencil sets:', error);
      }
    };

    fetchUserPencilSets();
  }, []);

  const userSetJournalOptions = useMemo(
    () => buildSetDropdownOptions(userPencilSetSizes),
    [userPencilSetSizes]
  );

  const journalUserSelectedSet = useMemo(() => {
    if (!journalFormData.userPencilSet) return null;
    const selectedId = journalFormData.userPencilSet.toString();
    const fromList = userPencilSets.find((set) => set.id?.toString() === selectedId);
    if (fromList) return fromList;
    if (userSet && userSet.id?.toString() === selectedId) return userSet;
    const fromSizes = userPencilSetSizes.find(
      (setSize) => (setSize.set?.id || setSize.id)?.toString() === selectedId
    );
    if (fromSizes) {
      return {
        id: fromSizes.set?.id || fromSizes.id,
        name: fromSizes.set?.name || 'Unknown',
        brand: fromSizes.set?.brand || 'Unknown'
      };
    }
    return { id: selectedId, name: 'Selected set', brand: 'Unknown' };
  }, [journalFormData.userPencilSet, userPencilSets, userSet, userPencilSetSizes]);

  // Video Set Selection Handlers
  const fetchVideoSetsForBrand = async (brandId) => {
    try {
      setLoadingVideoSets(true);
      const params = new URLSearchParams({ 
        page: '1', 
        per_page: '100',
        'filter[brand_id]': brandId.toString(),
        'filter[is_system]': '1',
        exclude_pencils: 'true'
      });
      const data = await apiGet(`/colored-pencil-sets?${params.toString()}`, true);
      
      let setsData = [];
      if (Array.isArray(data)) {
        setsData = data;
      } else if (data.data && Array.isArray(data.data)) {
        setsData = data.data;
      }
      
      const setsWithSizeCounts = setsData.map(set => ({
        ...set,
        sizeCount: set.sizes_count || 0
      }));
      setVideoSetsForBrand(setsWithSizeCounts);
    } catch (err) {
      console.error('Error fetching sets for brand:', err);
    } finally {
      setLoadingVideoSets(false);
    }
  };

  const fetchJournalVideoSetsForBrand = async (brandId) => {
    try {
      setLoadingJournalVideoSets(true);
      const params = new URLSearchParams({
        page: '1',
        per_page: '100',
        'filter[brand_id]': brandId.toString(),
        'filter[is_system]': '1',
        exclude_pencils: 'true'
      });
      const data = await apiGet(`/colored-pencil-sets?${params.toString()}`, true);

      let setsData = [];
      if (Array.isArray(data)) {
        setsData = data;
      } else if (data.data && Array.isArray(data.data)) {
        setsData = data.data;
      }

      const setsWithSizeCounts = setsData.map((set) => ({
        ...set,
        sizeCount: set.sizes_count || 0
      }));
      setJournalVideoSetsForBrand(setsWithSizeCounts);
      return setsWithSizeCounts;
    } catch (err) {
      console.error('Error fetching journal video sets for brand:', err);
      setJournalVideoSetsForBrand([]);
      return [];
    } finally {
      setLoadingJournalVideoSets(false);
    }
  };

  const handleJournalVideoBrandSelect = async (brand) => {
    setJournalVideoSelectedBrand(brand);
    setJournalVideoSelectedSet(null);
    setJournalFormData((prev) => ({ ...prev, videoPencilSet: '' }));
    await fetchJournalVideoSetsForBrand(brand.id);
    setJournalVideoStep('set');
  };

  const handleJournalVideoSetSelect = (set) => {
    setJournalVideoSelectedSet(set);
    setJournalFormData((prev) => ({ ...prev, videoPencilSet: set.id.toString() }));
    setJournalVideoStep('brand');
    setJournalVideoSelectedBrand(null);
    setJournalVideoSetsForBrand([]);
  };

  const handleJournalVideoBack = () => {
    if (journalVideoStep === 'set') {
      setJournalVideoStep('brand');
      setJournalVideoSelectedBrand(null);
      setJournalVideoSetsForBrand([]);
    }
  };

  // Sizes (with pencils) per set are cached so hovering, selecting and loading colors share one request
  const fetchVideoSizesForSet = useCallback((setId) => {
    const cache = videoSetSizesCacheRef.current;
    if (!cache.has(setId)) {
      const request = coloredPencilSetsAPI.getAvailableSetSizes(1, 100, true, {
        setId: setId,
        excludePencils: false
      })
        .then((response) => {
          const sizesForThisSet = Array.isArray(response) ? response : (response?.data || []);
          return sizesForThisSet.filter((setSize) => setSize.include_in_color_along !== false);
        })
        .catch((err) => {
          cache.delete(setId);
          throw err;
        });
      cache.set(setId, request);
    }
    return cache.get(setId);
  }, []);

  const prefetchVideoSet = (setId) => {
    fetchVideoSizesForSet(setId).catch(() => {});
  };

  const handleVideoBrandSelect = (brand) => {
    setVideoSelectedBrand(brand);
    setVideoSelectedSet(null);
    setVideoSetId(null);
    fetchVideoSetsForBrand(brand.id);
    setVideoStep('set');
  };

  const handleVideoSetSelect = async (set) => {
    if (pendingVideoSetId) return;
    setVideoSelectedSet(set);
    setPendingVideoSetId(set.id);
    let eligibleSizes;
    try {
      eligibleSizes = await fetchVideoSizesForSet(set.id);
    } catch (err) {
      console.error('Error fetching sizes for set:', err);
      setPendingVideoSetId(null);
      setVideoSelectedSet(null);
      return;
    }
    setPendingVideoSetId(null);
    setVideoIncludedSetSizeIds(eligibleSizes.map((size) => size.id));
    const uniquePencilIds = new Set(
      eligibleSizes.flatMap((size) => (Array.isArray(size.pencils) ? size.pencils : []))
        .map((pencil) => pencil?.id)
        .filter(Boolean)
    );

    setVideoSetId(set.id);
    // Reuse this state for selected video set display metadata
    setVideoSelectedSetSize({
      set: {
        id: set.id,
        name: set.name || 'Unknown',
        brand: set.brand || videoSelectedBrand?.name || 'Unknown',
        thumb: set.thumb || eligibleSizes.find((size) => size.thumb)?.thumb || null,
      },
      count: uniquePencilIds.size,
      eligible_sizes_count: eligibleSizes.length,
    });
    setVideoStep('brand');
    setVideoSelectedBrand(null);
    setVideoSelectedSet(null);
    setVideoSetsForBrand([]);
  };

  const handleVideoBack = () => {
    if (videoStep === 'set') {
      setVideoStep('brand');
      setVideoSelectedBrand(null);
      setVideoSelectedSet(null);
      setVideoSetsForBrand([]);
    }
  };

  // User Set Selection Handler (matches journal dropdown behavior)
  const handleUserSetDropdownSelect = (setIdValue) => {
    const parsedSetId = parseInt(setIdValue, 10);
    if (!parsedSetId) {
      setUserSetId(null);
      setUserSelectedSetSize(null);
      return;
    }

    setUserSetId(parsedSetId);

    // Keep a representative size for selected-set display metadata.
    const matchingSetSizes = userPencilSetSizes.filter(
      (setSize) => (setSize.set?.id || setSize.id)?.toString() === parsedSetId.toString()
    );
    const representativeSetSize = matchingSetSizes.sort((a, b) => (b.count || 0) - (a.count || 0))[0] || null;
    setUserSelectedSetSize(representativeSetSize);
  };

  const fetchInspirations = useCallback(async () => {
    try {
      setLoadingInspirations(true);
      const response = await inspirationAPI.getAll(1, 100, {
        sort: 'title',
        sort_direction: 'asc',
        archived: false
      });

      let inspirationsData = [];
      if (Array.isArray(response)) {
        inspirationsData = response;
      } else if (response.data && Array.isArray(response.data)) {
        inspirationsData = response.data;
      }

      setInspirations(inspirationsData);
    } catch (error) {
      console.error('Error fetching inspirations:', error);
    } finally {
      setLoadingInspirations(false);
    }
  }, []);

  useEffect(() => {
    fetchInspirations();
  }, [fetchInspirations]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedLibrarySearch(librarySearch.trim());
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [librarySearch]);

  const fetchPickerVideos = useCallback(async () => {
    try {
      setLoadingPickerVideos(true);
      const isSearch = Boolean(debouncedLibrarySearch);
      const response = await inspirationAPI.getAll(1, isSearch ? 20 : 4, {
        type: 'video',
        sort: 'created_at',
        sort_direction: 'desc',
        archived: false,
        ...(isSearch ? { search: debouncedLibrarySearch } : {}),
      });
      const videosData = Array.isArray(response) ? response : (response?.data ?? []);
      setPickerVideos(videosData);
    } catch (error) {
      console.error('Error fetching Color Along videos:', error);
      setPickerVideos([]);
    } finally {
      setLoadingPickerVideos(false);
    }
  }, [debouncedLibrarySearch]);

  const fetchPlaylists = useCallback(async () => {
    try {
      setLoadingPlaylists(true);
      const response = await playlistsAPI.getAll({ preview: true });
      const playlistsData = Array.isArray(response) ? response : (response?.data ?? []);
      setPlaylists(playlistsData);
    } catch (error) {
      console.error('Error fetching Color Along playlists:', error);
      setPlaylists([]);
    } finally {
      setLoadingPlaylists(false);
    }
  }, []);

  useEffect(() => {
    fetchPickerVideos();
  }, [fetchPickerVideos]);

  useEffect(() => {
    fetchPlaylists();
  }, [fetchPlaylists]);

  const refreshPickerLibrary = useCallback(() => {
    fetchInspirations();
    fetchPickerVideos();
    fetchPlaylists();
    if (activePlaylist?.id) {
      playlistsAPI.getVideos(activePlaylist.id)
        .then((response) => {
          const videos = Array.isArray(response) ? response : (response?.data ?? []);
          setPlaylistVideos(videos.map((video) => ({ ...video, type: 'video' })));
        })
        .catch((error) => console.error('Error refreshing playlist videos:', error));
    }
  }, [fetchInspirations, fetchPickerVideos, fetchPlaylists, activePlaylist?.id]);

  useYouTubeImportRefresh(refreshPickerLibrary);

  const filteredPlaylists = useMemo(() => {
    const query = debouncedLibrarySearch.toLowerCase();
    if (!query) {
      return playlists;
    }
    return playlists.filter((playlist) => (playlist.title || '').toLowerCase().includes(query));
  }, [playlists, debouncedLibrarySearch]);

  const filteredPlaylistVideos = useMemo(() => {
    const query = debouncedLibrarySearch.toLowerCase();
    if (!query) {
      return playlistVideos;
    }
    return playlistVideos.filter((video) => (video.title || '').toLowerCase().includes(query));
  }, [playlistVideos, debouncedLibrarySearch]);

  // Fetch colors for video set when selected
  useEffect(() => {
    const fetchVideoSetColors = async () => {
      if (!videoSetId) {
        setVideoSet(null);
        return;
      }

      try {
        setLoadingColors(true);
        const eligibleSizes = await fetchVideoSizesForSet(videoSetId);
        const pencilsById = new Map();
        eligibleSizes.forEach((setSize) => {
          (Array.isArray(setSize.pencils) ? setSize.pencils : []).forEach((pencil) => {
            if (pencil?.id && !pencilsById.has(pencil.id)) pencilsById.set(pencil.id, pencil);
          });
        });
        const pencilsData = [...pencilsById.values()];

        // Find the set info
        const setInfo = allPencilSets.find(set => set.id === videoSetId) || videoSelectedSetSizeRef.current?.set;
        
        // Transform pencils to colors format (API returns pencils ordered by name)
        const colors = pencilsData
          .filter(pencil => pencil.color && pencil.color.hex)
          .map((pencil, index) => ({
            id: pencil.id || index,
            name: pencil.color_name || pencil.color?.name || 'Unknown',
            hex: pencil.color.hex || '#000000',
            inStock: true
          }));

        if (cancelled) return;
        setVideoSet({
          id: videoSetId,
          name: setInfo?.name || 'Unknown',
          brand: setInfo?.brand || 'Unknown',
          count: colors.length,
          colors
        });
      } catch (error) {
        if (cancelled) return;
        console.error('Error fetching video set colors:', error);
        setVideoSet(null);
      } finally {
        setLoadingColors(false);
      }
    };

    let cancelled = false;
    fetchVideoSetColors();
    return () => {
      cancelled = true;
    };
  }, [videoSetId, allPencilSets, fetchVideoSizesForSet]);

  // Fetch colors for user set when selected
  useEffect(() => {
    const fetchUserSetColors = async () => {
      if (!userSetId) {
        setUserSet(null);
        return;
      }

      try {
        setLoadingColors(true);
        // Find the user set data
        const userSetData = userPencilSets.find(set => set.id === userSetId);
        
        if (!userSetData) {
          setUserSet(null);
          return;
        }

        // Try to get pencils from the stored set size data first
        const setSizeData = userPencilSetSizes.find(size => size.id === userSetData.setSizeId);
        
        let pencilsData = [];
        
        if (setSizeData && setSizeData.pencils && Array.isArray(setSizeData.pencils) && setSizeData.pencils.length > 0) {
          // Use pencils from set size if available
          pencilsData = setSizeData.pencils;
        } else {
          // Otherwise, fetch pencils using the set ID
          const pencilsResponse = await coloredPencilSetsAPI.getPencils(userSetId);
          
          // Handle response format
          if (Array.isArray(pencilsResponse)) {
            pencilsData = pencilsResponse;
          } else if (pencilsResponse.data && Array.isArray(pencilsResponse.data)) {
            pencilsData = pencilsResponse.data;
          }
        }

        // Transform pencils to colors format (API returns pencils ordered by name)
        const colors = pencilsData
          .filter(pencil => pencil.color && pencil.color.hex)
          .map((pencil, index) => ({
            id: pencil.id || index,
            name: pencil.color_name || pencil.color?.name || 'Unknown',
            hex: pencil.color.hex || '#000000',
            inStock: true
          }));

        setUserSet({
          id: userSetId,
          name: userSetData.name,
          brand: userSetData.brand,
          count: colors.length,
          colors
        });
      } catch (error) {
        console.error('Error fetching user set colors:', error);
        setUserSet(null);
      } finally {
        setLoadingColors(false);
      }
    };

    fetchUserSetColors();
  }, [userSetId, userPencilSets, userPencilSetSizes]);

  useEffect(() => {
    const inspirationId = currentInspiration?.id;
    if (!inspirationId) {
      setExistingJournalEntry(null);
      return;
    }

    let cancelled = false;
    const fetchExistingLog = async () => {
      try {
        const response = await journalEntriesAPI.getAll({ inspiration_id: inspirationId });
        const items = Array.isArray(response) ? response : (response?.data || []);
        if (!cancelled) {
          setExistingJournalEntry(items[0] || null);
        }
      } catch (error) {
        console.error('Error fetching existing coloring log:', error);
        if (!cancelled) {
          setExistingJournalEntry(null);
        }
      }
    };

    fetchExistingLog();
    return () => {
      cancelled = true;
    };
  }, [currentInspiration?.id]);

  // Fetch journal entry data when modal opens
  useEffect(() => {
    const fetchJournalData = async () => {
      if (!showJournalModal) return;

      try {
        setLoadingJournalData(true);

        // Fetch palettes with sorting and archived filter
        const palettesResponse = await colorPalettesAPI.getAll(1, 100, {
          sort: 'title',
          sort_direction: 'asc',
          archived: false
        });
        let palettesData = [];
        if (Array.isArray(palettesResponse)) {
          palettesData = palettesResponse;
        } else if (palettesResponse.data && Array.isArray(palettesResponse.data)) {
          palettesData = palettesResponse.data;
        }
        setPalettes(palettesData);

        // Fetch combos with sorting and archived filter
        const combosResponse = await colorCombosAPI.getAll(1, 100, {
          sort: 'title',
          sort_direction: 'asc',
          archived: false
        });
        let combosData = [];
        if (Array.isArray(combosResponse)) {
          combosData = combosResponse;
        } else if (combosResponse.data && Array.isArray(combosResponse.data)) {
          combosData = combosResponse.data;
        }
        setCombos(combosData);
      } catch (error) {
        console.error('Error fetching journal data:', error);
      } finally {
        setLoadingJournalData(false);
      }
    };

    fetchJournalData();
  }, [showJournalModal]);

  // Pre-populate journal form when modal opens
  useEffect(() => {
    if (!showJournalModal) {
      journalDraftInitializedRef.current = false;
      return;
    }
    // Only pre-fill once per open so changing the video or sets mid-session doesn't wipe the draft
    if (journalDraftInitializedRef.current) {
      return;
    }
    journalDraftInitializedRef.current = true;
    journalDraftKeyRef.current = journalInspirationKey;
    setJournalMinimized(false);
    setJournalVideoStartSeconds(getYouTubeTimeSeconds());

    // Initialize form with defaults
    let currentInspirationId = '';

    // Find current inspiration (video or image) if inspirations are loaded
    if (inspirations.length > 0) {
      if (selectedVideo) {
        // Find video in inspirations by embed_id or id
        const videoInspiration = inspirations.find(insp => {
          if (insp.type === 'video') {
            return insp.embed_id === selectedVideo.id || insp.embed_id === selectedVideo.embed_id || insp.id === selectedVideo.id;
          }
          return false;
        });
        if (videoInspiration) {
          currentInspirationId = getInspirationKey(videoInspiration);
        }
      } else if (selectedImage) {
        // Find image in inspirations by id
        const imageInspiration = inspirations.find(insp => {
          if (insp.type === 'file' || insp.type === 'image' || insp.type === 'pdf') {
            return insp.id === selectedImage.id || insp.id === parseInt(selectedImage.id, 10);
          }
          return false;
        });
        if (imageInspiration) {
          currentInspirationId = getInspirationKey(imageInspiration);
        }
      }
    }

    const preselectedVideoSetId = videoSetId ? videoSetId.toString() : '';

    setJournalFormData({
      date: getLocalDateString(),
      inspiration: currentInspirationId,
      videoPencilSet: preselectedVideoSetId,
      userPencilSet: userSetId ? userSetId.toString() : '',
      book: urlBookId,
      bookPage: EMPTY_BOOK_PAGE,
      palette: '',
      combos: [],
      tags: [],
      notes: ''
    });
    setJournalVideoStep('brand');
    setJournalVideoSelectedBrand(null);
    setJournalVideoSetsForBrand([]);
    setJournalVideoSelectedSet(
      preselectedVideoSetId
        ? {
            id: parseInt(preselectedVideoSetId, 10),
            name: videoSelectedSetSize?.set?.name || videoSelectedSet?.name || 'Selected set',
            brand: videoSelectedSetSize?.set?.brand || videoSelectedSet?.brand || 'Unknown'
          }
        : null
    );
  }, [showJournalModal, selectedVideo, selectedImage, videoSetId, userSetId, inspirations, videoSelectedSetSize, videoSelectedSet, journalInspirationKey, urlBookId, getYouTubeTimeSeconds]);

  useEffect(() => {
    if (!showJournalModal || !existingJournalEntry) {
      return;
    }
    setJournalFormData((prev) => ({
      ...prev,
      videoPencilSet: prev.videoPencilSet || (existingJournalEntry.video_colored_pencil_set_id ? String(existingJournalEntry.video_colored_pencil_set_id) : ''),
      userPencilSet: prev.userPencilSet || (existingJournalEntry.user_colored_pencil_set_id || existingJournalEntry.colored_pencil_set_id
        ? String(existingJournalEntry.user_colored_pencil_set_id || existingJournalEntry.colored_pencil_set_id)
        : ''),
      book: prev.book || (existingJournalEntry.book_id ? String(existingJournalEntry.book_id) : ''),
      palette: prev.palette || (existingJournalEntry.color_palette_id ? String(existingJournalEntry.color_palette_id) : ''),
      combos: prev.combos?.length ? prev.combos : (existingJournalEntry.combos || []).map((id) => String(id)),
      tags: prev.tags?.length ? prev.tags : (existingJournalEntry.tags || []),
    }));
    if (existingJournalEntry.video_colored_pencil_set_id) {
      setJournalVideoSelectedSet((prev) => prev || {
        id: existingJournalEntry.video_colored_pencil_set_id,
        name: 'Selected set',
        brand: 'Unknown',
      });
    }
  }, [showJournalModal, existingJournalEntry]);

  // Update inspiration when inspirations load and we have a selected video/image
  useEffect(() => {
    if (showJournalModal && inspirations.length > 0 && journalFormData.inspiration === '') {
      let currentInspirationId = '';
      if (selectedVideo) {
        const videoInspiration = inspirations.find(insp => 
          insp.type === 'video' && (insp.embed_id === selectedVideo.id || insp.embed_id === selectedVideo.embed_id || insp.id === selectedVideo.id)
        );
        if (videoInspiration) {
          currentInspirationId = getInspirationKey(videoInspiration);
        }
      } else if (selectedImage) {
        const imageInspiration = inspirations.find(insp =>
          (insp.type === 'file' || insp.type === 'image' || insp.type === 'pdf')
          && (insp.id === selectedImage.id || insp.id === parseInt(selectedImage.id, 10))
        );
        if (imageInspiration) {
          currentInspirationId = getInspirationKey(imageInspiration);
        }
      }
      
      if (currentInspirationId) {
        setJournalFormData(prev => ({
          ...prev,
          inspiration: currentInspirationId
        }));
      }
    }
  }, [inspirations, showJournalModal, selectedVideo, selectedImage]);

  // Discard the draft when the selected video/image changes, and restore it when the same one is selected again
  // (e.g. after a refresh). Must stay below the pre-populate effect so a restored draft isn't overwritten.
  useEffect(() => {
    const previousKey = previousJournalInspirationKeyRef.current;
    previousJournalInspirationKeyRef.current = journalInspirationKey;

    if (previousKey && previousKey !== journalInspirationKey) {
      clearJournalDraft();
      journalDraftKeyRef.current = null;
      setShowJournalModal(false);
      return;
    }

    if (!journalInspirationKey || !user?.id || journalDraftKeyRef.current === journalInspirationKey) {
      return;
    }

    const draft = readJournalDraft();
    if (!draft) {
      return;
    }
    if (draft.userId !== user.id || draft.inspirationKey !== journalInspirationKey) {
      clearJournalDraft();
      return;
    }

    journalDraftInitializedRef.current = true;
    journalDraftKeyRef.current = journalInspirationKey;
    setJournalFormData(draft.formData);
    setJournalVideoSelectedSet(draft.videoSelectedSet ?? null);
    setJournalVideoStartSeconds(draft.videoStartSeconds ?? null);
    setJournalMinimized(Boolean(draft.minimized));
    setShowJournalModal(true);
  }, [journalInspirationKey, user?.id]);

  useEffect(() => {
    const draftKey = journalDraftKeyRef.current;
    if (!showJournalModal || !user?.id || !draftKey || draftKey !== journalInspirationKey) {
      return;
    }
    writeJournalDraft({
      userId: user.id,
      inspirationKey: draftKey,
      formData: journalFormData,
      videoSelectedSet: journalVideoSelectedSet,
      videoStartSeconds: journalVideoStartSeconds,
      minimized: journalMinimized,
      updatedAt: Date.now(),
    });
  }, [showJournalModal, journalFormData, journalVideoSelectedSet, journalVideoStartSeconds, journalMinimized, journalInspirationKey, user?.id]);

  const discardJournalDraft = () => {
    clearJournalDraft();
    journalDraftKeyRef.current = null;
    setJournalVideoStartSeconds(null);
    setShowJournalModal(false);
  };

  const journalActionLabel = showJournalModal
    ? 'Resume Log'
    : (existingJournalEntry ? 'Continue Log' : 'Add Journal Entry');

  // Fetch matches using backend API when both sets are selected
  useEffect(() => {
    const fetchMatches = async () => {
      if (!videoSet || !userSet || !videoSetId || !userSetId) {
        setMatches([]);
        return;
      }

      try {
        setLoadingMatches(true);
        const compareOptions = videoIncludedSetSizeIds.length > 0
          ? { sourceSetSizeIds: videoIncludedSetSizeIds }
          : {};
        const result = await coloredPencilSetsAPI.compare(videoSetId, userSetId, includeTwoColorMix, compareOptions);
        
        // Handle response structure - check if matches exists and is an array
        const matchesArray = result?.matches || result?.data?.matches || [];
        
        if (!Array.isArray(matchesArray)) {
          console.error('Invalid matches format from API:', result);
          setMatches([]);
          setLoadingMatches(false);
          return;
        }
        
        // Transform API response to match expected format
        const transformedMatches = matchesArray.map(match => {
          const videoColor = {
            id: match.source_pencil.id,
            name: match.source_pencil.color_name || match.source_pencil.color?.name || 'Unknown',
            hex: normalizeHex(match.source_pencil.color?.hex),
            color_number: match.source_pencil.color_number,
          };

          if (match.is_mix && match.target_pencil_mix) {
            // Two-color mix match
            return {
              videoColor,
              match: {
                is_mix: true,
                color1: {
                  id: match.target_pencil_mix.color1.id,
                  name: match.target_pencil_mix.color1.color_name || match.target_pencil_mix.color1.color?.name || 'Unknown',
                  hex: normalizeHex(match.target_pencil_mix.color1.color?.hex),
                  color_number: match.target_pencil_mix.color1.color_number,
                  inventory: match.target_pencil_mix.color1.inventory ?? 0,
                },
                color2: {
                  id: match.target_pencil_mix.color2.id,
                  name: match.target_pencil_mix.color2.color_name || match.target_pencil_mix.color2.color?.name || 'Unknown',
                  hex: normalizeHex(match.target_pencil_mix.color2.color?.hex),
                  color_number: match.target_pencil_mix.color2.color_number,
                  inventory: match.target_pencil_mix.color2.inventory ?? 0,
                },
                mixed_hex: normalizeHex(match.target_pencil_mix.mixed_hex),
                ratio: match.target_pencil_mix.ratio,
                delta_e: match.delta_e,
                match_quality: match.match_quality,
              },
            };
          } else if (match.target_pencil) {
            // Single color match
            return {
              videoColor,
              match: {
                is_mix: false,
                id: match.target_pencil.id,
                name: match.target_pencil.color_name || match.target_pencil.color?.name || 'Unknown',
                hex: normalizeHex(match.target_pencil.color?.hex),
                color_number: match.target_pencil.color_number,
                inventory: match.target_pencil.inventory ?? 0,
                delta_e: match.delta_e,
                match_quality: match.match_quality,
                distance: match.delta_e, // For backwards compatibility
              },
            };
          } else {
            // No match found (shouldn't happen, but handle gracefully)
            return null;
          }
        }).filter(match => match !== null); // Filter out any null matches

        setMatches(transformedMatches);
        
        // Group matches by index character for navigation
        const groupedMatches = {};
        transformedMatches.forEach((match) => {
          const char = getIndexChar(match.videoColor.name);
          if (!groupedMatches[char]) {
            groupedMatches[char] = [];
          }
          groupedMatches[char].push(match);
        });
        
        // Store grouped matches for index navigation
        setGroupedMatches(groupedMatches);
      } catch (error) {
        console.error('Error fetching matches:', error);
        // Fallback to client-side matching
        const fallbackMatches = videoSet.colors.map(videoColor => ({
          videoColor,
          match: findClosestColor(videoColor, userSet)
        }));
        setMatches(fallbackMatches);
      } finally {
        setLoadingMatches(false);
      }
    };

    fetchMatches();
  }, [videoSet, userSet, videoSetId, userSetId, includeTwoColorMix, videoIncludedSetSizeIds]);

  // Fetch two-color mix for a specific row
  const fetchRowMix = async (videoColorId) => {
    if (!videoSetId || !userSetId || !videoSet || !userSet) return;

    try {
      setLoadingRowMix(prev => ({ ...prev, [videoColorId]: true }));
      
      // Find the video color
      const videoColor = videoSet.colors.find(c => c.id === videoColorId);
      if (!videoColor) return;
      
      // Call compare API with two-color mix enabled
      const compareOptions = videoIncludedSetSizeIds.length > 0
        ? { sourceSetSizeIds: videoIncludedSetSizeIds }
        : {};
      const result = await coloredPencilSetsAPI.compare(videoSetId, userSetId, true, compareOptions);
      
      // Find the match for this specific video color by matching hex
      const matchesArray = result?.matches || result?.data?.matches || [];
      const exactMatch = matchesArray.find(m => {
        const sourceHex = normalizeHex(m.source_pencil?.color?.hex);
        const targetHex = normalizeHex(videoColor.hex);
        return sourceHex === targetHex;
      });
      
      if (exactMatch && exactMatch.is_mix && exactMatch.target_pencil_mix) {
        // Transform to match expected format
        const mixMatch = {
          is_mix: true,
          color1: {
            id: exactMatch.target_pencil_mix.color1.id,
            name: exactMatch.target_pencil_mix.color1.color_name || exactMatch.target_pencil_mix.color1.color?.name || 'Unknown',
            hex: normalizeHex(exactMatch.target_pencil_mix.color1.color?.hex),
            color_number: exactMatch.target_pencil_mix.color1.color_number,
            inventory: exactMatch.target_pencil_mix.color1.inventory ?? 0,
          },
          color2: {
            id: exactMatch.target_pencil_mix.color2.id,
            name: exactMatch.target_pencil_mix.color2.color_name || exactMatch.target_pencil_mix.color2.color?.name || 'Unknown',
            hex: normalizeHex(exactMatch.target_pencil_mix.color2.color?.hex),
            color_number: exactMatch.target_pencil_mix.color2.color_number,
            inventory: exactMatch.target_pencil_mix.color2.inventory ?? 0,
          },
          mixed_hex: normalizeHex(exactMatch.target_pencil_mix.mixed_hex),
          ratio: exactMatch.target_pencil_mix.ratio,
          delta_e: exactMatch.delta_e,
          match_quality: exactMatch.match_quality,
        };
        
        setRowMixMatches(prev => ({ ...prev, [videoColorId]: mixMatch }));
      }
    } catch (error) {
      console.error('Error fetching row mix:', error);
    } finally {
      setLoadingRowMix(prev => ({ ...prev, [videoColorId]: false }));
    }
  };

  // Toggle two-color mix for a specific row
  const toggleRowMix = (videoColorId) => {
    if (rowMixMatches[videoColorId]) {
      // Remove mix match
      setRowMixMatches(prev => {
        const updated = { ...prev };
        delete updated[videoColorId];
        return updated;
      });
    } else {
      // Fetch mix match
      fetchRowMix(videoColorId);
    }
  };

  // Mark pencil(s) as out (set inventory to 0)
  const handleMarkAsOut = async (pencilIds) => {
    // Ensure pencilIds is an array
    const ids = Array.isArray(pencilIds) ? pencilIds : [pencilIds];
    
    // Set loading state for all pencils being marked
    setMarkingOut(prev => {
      const updated = { ...prev };
      ids.forEach(id => {
        updated[id] = true;
      });
      return updated;
    });

    try {
      // Update inventory to 0 for all pencils
      await Promise.all(ids.map(id => coloredPencilsAPI.updateInventory(id, 0)));
      
      // Show success message
      alert('Pencil(s) marked as out. They will be added to your shopping list.');
    } catch (error) {
      console.error('Error marking pencil as out:', error);
      alert('Failed to mark pencil(s) as out. Please try again.');
    } finally {
      // Clear loading state
      setMarkingOut(prev => {
        const updated = { ...prev };
        ids.forEach(id => {
          delete updated[id];
        });
        return updated;
      });
    }
  };

  const handlePlaylistSelect = async (playlist) => {
    setActivePlaylist(playlist);
    setLoadingPlaylistVideos(true);
    setPlaylistVideos([]);
    try {
      const response = await playlistsAPI.getVideos(playlist.id);
      const videos = Array.isArray(response) ? response : (response?.data ?? []);
      setPlaylistVideos(videos.map((video) => ({ ...video, type: 'video' })));
    } catch (error) {
      console.error('Error fetching playlist videos:', error);
      setPlaylistVideos([]);
    } finally {
      setLoadingPlaylistVideos(false);
    }
  };

  const handleInspirationSelect = (inspiration) => {
    if (inspiration.type === 'video') {
      setSelectedVideo({
        id: inspiration.embed_id,
        embed_id: inspiration.embed_id,
        title: inspiration.title || 'Video'
      });
      setVideoId(inspiration.embed_id);
      setSelectedImage(null);
      // Put the video (and its saved pencil set/book) on the URL so the set is applied and the journal preselects the book
      navigate(buildColorAlongVideoPath({
        embedId: inspiration.embed_id,
        pencilSetSizeId: inspiration.colored_pencil_set_size_id,
        bookId: inspiration.book_id,
      }));
    } else if (inspiration.type === 'file') {
      setSelectedImage({
        id: inspiration.id,
        title: inspiration.title || 'Image',
        thumbnail: inspiration.thumbnail_path || inspiration.path,
        path: inspiration.path
      });
      setSelectedVideo(null);
      setVideoId('');
    }
    // Close the left navigation when an inspiration is clicked
    if (onInspirationClick) {
      onInspirationClick();
    }
  };

  const renderVideoCard = (inspiration) => (
    <button
      key={`${inspiration.type || 'video'}-${inspiration.id || inspiration.embed_id}`}
      onClick={() => handleInspirationSelect(inspiration)}
      className="bg-slate-50 rounded-xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-md transition-all text-left p-0 flex flex-col"
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#ea3663'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; }}
    >
      <div className="relative aspect-video bg-slate-100 overflow-hidden">
        <img
          src={inspiration.thumb || `https://img.youtube.com/vi/${inspiration.embed_id}/hqdefault.jpg`}
          alt={inspiration.title || 'Video'}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.src = 'https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=400&h=300&fit=crop';
          }}
        />
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
          <div className="w-10 h-10 rounded-full bg-white bg-opacity-90 flex items-center justify-center">
            <svg className="w-5 h-5 ml-0.5" fill="currentColor" viewBox="0 0 24 24" style={{ color: '#49817b' }}>
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </div>
      </div>
      <div className="p-2">
        <p className="text-sm font-medium text-slate-800 line-clamp-2">{inspiration.title || 'Video'}</p>
      </div>
    </button>
  );

  const handleLoadVideo = async () => {
    const resolvedVideoId = extractYouTubeVideoId(videoId);
    if (!resolvedVideoId) {
      setVideoLoadError('Enter a valid YouTube URL or video ID.');
      return;
    }

    setVideoLoadError(null);
    setLoadingVideo(true);
    setVideoId(resolvedVideoId);

    try {
      const created = await videosAPI.create({ embed_id: resolvedVideoId });
      const saved = created?.data ?? created;
      setSelectedVideo({
        id: saved?.embed_id || resolvedVideoId,
        embed_id: saved?.embed_id || resolvedVideoId,
        title: saved?.title || 'Custom Video',
        inspirationId: saved?.id,
      });
      if (loadVideoPlaylistId && saved?.id) {
        try {
          await videosAPI.addToPlaylist(saved.id, Number(loadVideoPlaylistId));
          setLoadVideoPlaylistId('');
          fetchPlaylists();
        } catch (playlistError) {
          console.error('Error adding loaded video to playlist:', playlistError);
          setVideoLoadError(playlistError.data?.message || 'Video loaded, but it could not be added to the playlist.');
        }
      }
      fetchInspirations();
      fetchPickerVideos();
    } catch (error) {
      console.error('Error saving loaded video to inspiration:', error);
      setSelectedVideo({ id: resolvedVideoId, title: 'Custom Video' });
      setVideoLoadError(error.data?.message || error.message || 'Video loaded, but it could not be saved to your inspiration.');
    } finally {
      setLoadingVideo(false);
    }
  };

  const collapseMatchesPanel = () => {
    if (matchesScrollRef.current) {
      matchesScrollPositionRef.current = matchesScrollRef.current.scrollTop;
    }
    setIsMatchesCollapsed(true);
  };

  const expandMatchesPanel = () => {
    setIsMatchesCollapsed(false);
    requestAnimationFrame(() => {
      if (matchesScrollRef.current) {
        matchesScrollRef.current.scrollTop = matchesScrollPositionRef.current;
      }
    });
  };

  return (
    <div 
      className="w-full"
      style={{ 
        height: 'calc(100vh - 72px)',
        minHeight: '600px',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Pencil Set Selection and Video Selection Section */}
      <div
        className={`grid gap-2 ${
          isMatchesCollapsed
            ? 'grid-cols-1 lg:grid-cols-[3rem_minmax(0,1fr)]'
            : 'grid-cols-1 lg:grid-cols-4'
        }`}
        style={{
          height: '100%',
          minHeight: '600px'
        }}
      >
        {/* Left Column: Pencil Sets and Color Matches */}
        <div
          className="lg:col-span-1 flex flex-col transition-all duration-200"
          style={{ minHeight: '600px', height: '100%', maxHeight: '100%' }}
        >
          <div className="h-full flex flex-col overflow-hidden">
            {isMatchesCollapsed ? (
              <div className="bg-slate-50 shadow-sm border border-slate-200 h-full flex items-center justify-center">
                <button
                  onClick={expandMatchesPanel}
                  className="h-full w-full flex flex-col items-center justify-center gap-2 text-slate-700 hover:text-slate-900 transition-colors py-3"
                  aria-label="Expand pencil sets and color matches panel"
                  title="Expand panel"
                >
                  <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <span
                    className="text-[10px] font-semibold text-slate-800 font-venti tracking-wide"
                    style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
                  >
                    Color Conversion
                  </span>
                </button>
              </div>
            ) : (
              <>
            <div className="bg-white p-1.5 border border-slate-200 border-b-0 flex items-center justify-between flex-shrink-0">
              <h3 className="text-xs font-semibold text-slate-800 font-venti">Color Conversion</h3>
              <button
                onClick={() => {
                  setShowMatchesMenu(false);
                  collapseMatchesPanel();
                }}
                className="p-1 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded transition-colors"
                aria-label="Collapse pencil sets and color matches panel"
                title="Collapse panel"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            </div>
            {/* Pencil Set Selection Section */}
            {(!videoSetId || !userSetId) && (
              <div className="bg-white p-3 flex-1 min-h-0 border-b border-slate-200 overflow-y-auto pb-12">
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Pencil Sets</label>
                <div className="space-y-1.5">
                  {/* Video Set Selection */}
                  <div className="bg-slate-50 shadow-sm border border-slate-200 p-1.5 mb-8">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="text-xs font-semibold text-slate-800 font-venti">Used In Video</h4>
                      {videoSetId && (
                        <button
                          onClick={() => {
                            setVideoSetId(null);
                            setVideoSelectedSetSize(null);
                            setVideoIncludedSetSizeIds([]);
                            setVideoStep('brand');
                            setVideoSelectedBrand(null);
                            setVideoSelectedSet(null);
                            setVideoSetsForBrand([]);
                          }}
                          className="text-xs text-slate-500 hover:text-slate-700 underline"
                        >
                          Change
                        </button>
                      )}
                    </div>
                    
                    {videoSetId && videoSelectedSetSize ? (
                      <div className="flex items-center gap-2 p-2 bg-white rounded text-xs">
                        <ListThumb
                          src={videoSelectedSetSize.thumb || videoSelectedSetSize.set?.thumb}
                          alt={videoSelectedSetSize.set?.name || videoSelectedSetSize.name || 'Set'}
                          icon="set"
                          className="w-9 h-9"
                        />
                        <div className="min-w-0">
                          <p className="font-medium text-slate-800 truncate">{videoSelectedSetSize.set?.name || videoSelectedSetSize.name}</p>
                          <p className="text-slate-600 mt-0.5 truncate">
                            {videoSelectedSetSize.set?.brand || videoSelectedSetSize.brand} - {videoSelectedSetSize.count || 0} pencils
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {videoStep !== 'brand' && (
                          <button
                            onClick={handleVideoBack}
                            disabled={Boolean(pendingVideoSetId)}
                            className="flex items-center space-x-1 text-xs text-slate-600 hover:text-slate-800 transition-colors disabled:opacity-50"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                            <span>Back</span>
                          </button>
                        )}
                        
                        <div className="text-xs font-medium text-slate-700 mb-1">
                          {videoStep === 'brand' && 'Select a Brand'}
                          {videoStep === 'set' && `Select a Set for ${videoSelectedBrand?.name || ''}`}
                        </div>

                        {/* Step 1: Brand Selection */}
                        {videoStep === 'brand' && (
                          <div className="border border-slate-200 rounded p-1.5 max-h-40 overflow-y-auto">
                            {loadingBrands ? (
                              <div className="text-center py-2 text-xs text-slate-500">Loading brands...</div>
                            ) : brands.length === 0 ? (
                              <div className="text-center py-2 text-xs text-slate-500">No brands available</div>
                            ) : (
                              <div className="space-y-1">
                                {brands.map((brand) => (
                                  <button
                                    key={brand.id}
                                    onClick={() => handleVideoBrandSelect(brand)}
                                    className="w-full flex items-center gap-2 p-1.5 rounded border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-colors text-left"
                                  >
                                    <ListThumb src={brand.thumbnail} alt={brand.name} icon="brand" />
                                    <span className="flex-1 min-w-0 text-xs font-medium text-slate-800 truncate">{brand.name}</span>
                                    <svg className="w-3 h-3 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Step 2: Set Selection */}
                        {videoStep === 'set' && videoSelectedBrand && (
                          <div className="border border-slate-200 rounded p-1.5 max-h-40 overflow-y-auto">
                            {loadingVideoSets ? (
                              <div className="text-center py-2 text-xs text-slate-500">Loading sets...</div>
                            ) : videoSetsForBrand.length === 0 ? (
                              <div className="text-center py-2 text-xs text-slate-500">No sets available for this brand</div>
                            ) : (
                              <div className="space-y-1" aria-busy={Boolean(pendingVideoSetId)}>
                                {videoSetsForBrand.map((set) => {
                                  const isPending = pendingVideoSetId === set.id;
                                  const isDisabled = Boolean(pendingVideoSetId) && !isPending;
                                  return (
                                    <button
                                      key={set.id}
                                      onClick={() => handleVideoSetSelect(set)}
                                      onMouseEnter={() => prefetchVideoSet(set.id)}
                                      onFocus={() => prefetchVideoSet(set.id)}
                                      disabled={isDisabled}
                                      aria-busy={isPending}
                                      className={`w-full flex items-center gap-2 p-1.5 rounded border transition-colors text-left ${
                                        isPending
                                          ? 'border-pink-300 bg-pink-50 cursor-wait'
                                          : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                                      } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                      <ListThumb src={set.thumb} alt={set.name || 'Set'} icon="set" />
                                      <div className="flex-1 min-w-0">
                                        <div className="text-xs font-medium text-slate-800 truncate">
                                          {set.name || 'Unknown'}
                                        </div>
                                        {isPending && (
                                          <div className="text-[11px] text-pink-600">Loading pencils...</div>
                                        )}
                                      </div>
                                      {isPending ? (
                                        <svg className="w-3.5 h-3.5 text-pink-500 animate-spin flex-shrink-0" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                                        </svg>
                                      ) : (
                                        <svg className="w-3 h-3 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* User Set Selection */}
                  <div className="bg-slate-75 shadow-sm border border-slate-200 p-1.5">
                    <div className="flex items-center justify-between mb-1.5">
                      <h4 className="text-xs font-semibold text-slate-800 font-venti">Your Pencil Set</h4>
                    </div>
                    <DropdownMenu
                      options={userSetJournalOptions}
                      value={userSetId ? userSetId.toString() : ''}
                      onChange={handleUserSetDropdownSelect}
                      placeholder="Select your pencil set..."
                    />
                  </div>
                </div>
              </div>
            )}

          {/* Color Matches Section - Only show when both sets are selected */}
          {videoSet && userSet && (
            <div className="bg-white flex-1 flex flex-col min-h-0 overflow-hidden" style={{ minHeight: 0 }}>
              <div className="bg-slate-50 shadow-sm border border-slate-200 overflow-hidden flex flex-col h-full">
                <div className="p-1.5 border-b border-slate-200 flex-shrink-0 relative">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xs font-semibold text-slate-800 font-venti">Color Matches</h3>
                      {matches.length > 0 && userSet && (
                        <p className="text-xs text-slate-600 mt-0.5">
                          Matches from {userSet.name}
                        </p>
                      )}
                    </div>
                    <div className="relative">
                        <button
                          onClick={() => setShowMatchesMenu(!showMatchesMenu)}
                          className="p-1 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded transition-colors"
                          aria-label="Menu"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                          </svg>
                        </button>
                        
                        {showMatchesMenu && (
                          <>
                            {/* Backdrop to close menu */}
                            <div 
                              className="fixed inset-0 z-10" 
                              onClick={() => setShowMatchesMenu(false)}
                            ></div>
                            
                            {/* Dropdown menu */}
                            <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-slate-200 z-20 py-1">
                              <label className="flex items-center px-3 py-2 hover:bg-slate-50 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={includeTwoColorMix}
                                  onChange={(e) => {
                                    setIncludeTwoColorMix(e.target.checked);
                                    setShowMatchesMenu(false);
                                  }}
                                  className="theme-checkbox theme-checkbox-small"
                                />
                                <span className="ml-2 text-xs text-slate-700">
                                  Include two-color mixes
                                </span>
                              </label>
                              {matches.length > 0 && (
                                <button
                                  onClick={() => {
                                    setVideoSetId(null);
                                    setUserSetId(null);
                                    setShowMatchesMenu(false);
                                  }}
                                  className="w-full text-left px-3 py-2 text-xs text-slate-600 hover:bg-slate-50 transition-colors"
                                >
                                  Change Sets
                                </button>
                              )}
                            </div>
                          </>
                        )}
                    </div>
                  </div>
                </div>

                <div className="flex flex-1 overflow-hidden min-h-0">
                  {/* Vertical Index Navigation */}
                  {matches.length > 0 && Object.keys(groupedMatches).length > 0 && (
                    <div className="flex-shrink-0 border-r border-slate-200 bg-slate-50 overflow-y-auto" style={{ width: '32px', maxHeight: '100%' }}>
                      <div className="py-2 flex flex-col items-center space-y-0.5">
                        {Object.keys(groupedMatches)
                          .sort((a, b) => {
                            // Sort: numbers first, then letters, then '#'
                            if (a === '#') return 1;
                            if (b === '#') return -1;
                            if (/[0-9]/.test(a) && /[A-Z]/.test(b)) return -1;
                            if (/[A-Z]/.test(a) && /[0-9]/.test(b)) return 1;
                            return a.localeCompare(b);
                          })
                          .map((char) => (
                            <button
                              key={char}
                              onClick={() => scrollToSection(char)}
                              className="w-6 h-6 flex items-center justify-center text-xs font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-200 rounded transition-colors"
                              title={`Scroll to ${char}`}
                            >
                              {char}
                            </button>
                          ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Matches Content */}
                  <div ref={matchesScrollRef} className="p-3 flex-1 overflow-y-auto min-h-0" style={{ backgroundColor: '#ffffff' }}>
                    {loadingColors || loadingMatches ? (
                      <div className="flex items-center justify-center h-full text-center p-4">
                        <div>
                          <p className="text-sm text-slate-400 mb-1">Loading colors...</p>
                        </div>
                      </div>
                    ) : matches.length > 0 ? (
                      <div className="space-y-1.5">
                        {Object.keys(groupedMatches)
                          .sort((a, b) => {
                            // Sort: numbers first, then letters, then '#'
                            if (a === '#') return 1;
                            if (b === '#') return -1;
                            if (/[0-9]/.test(a) && /[A-Z]/.test(b)) return -1;
                            if (/[A-Z]/.test(a) && /[0-9]/.test(b)) return 1;
                            return a.localeCompare(b);
                          })
                          .map((char) => (
                            <div key={char} id={`color-section-${char}`} className="scroll-mt-2">
                              {groupedMatches[char].map(({ videoColor, match }) => {
                                // Use per-row mix match if available, otherwise use regular match
                                const displayMatch = rowMixMatches[videoColor.id] || match;
                                const isRowMixActive = !!rowMixMatches[videoColor.id];
                                const isLoadingRowMix = loadingRowMix[videoColor.id];
                                
                                // Calculate match percentage for the original match (not the mix)
                                const matchPercentage = match.delta_e !== undefined 
                                  ? deltaEToPercentage(match.delta_e) 
                                  : null;
                                const showTwoColorMixButton = !match.is_mix && (matchPercentage === null || matchPercentage < 95);
                                
                                return (
                        <div
                          key={videoColor.id}
                          className="bg-white p-1.5 border border-slate-200 hover:shadow-md hover:scale-105 transition-all"
                          onMouseEnter={(e) => e.currentTarget.style.borderColor = '#ea3663'}
                          onMouseLeave={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
                        >
                          <div className="flex items-start gap-3">
                            {/* Video Color */}
                            <div className="w-1/2">
                              <p className="text-xs font-medium text-slate-600 mb-1">Video</p>
                              <div className="flex items-center gap-1.5">
                                <div
                                  className="w-7 h-7 rounded-lg shadow-sm border border-slate-200 flex-shrink-0"
                                  style={{ backgroundColor: videoColor.hex }}
                                ></div>
                                <div className="flex-1 min-w-0" style={{ maxWidth: '100px' }}>
                                  <p className="text-xs font-medium text-slate-800 truncate">{videoColor.name}</p>
                                  {videoColor.color_number && (
                                    <p className="text-xs text-slate-500 font-mono truncate">{videoColor.color_number}</p>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Match Color - Handle both single and two-color mix */}
                            <div className="w-1/2">
                              <div className="flex items-center justify-between mb-1">
                                <p className="text-xs font-medium text-slate-600">Your Match</p>
                                <div className="flex items-center gap-1">
                                  {showTwoColorMixButton && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleRowMix(videoColor.id);
                                      }}
                                      disabled={isLoadingRowMix}
                                      className="p-0.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                      title={isRowMixActive ? "Hide two-color mix" : "Show two-color mix"}
                                    >
                                      {isLoadingRowMix ? (
                                        <svg className="w-3.5 h-3.5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                        </svg>
                                      ) : (
                                        <svg 
                                          className="w-3.5 h-3.5" 
                                          fill="currentColor" 
                                          viewBox="0 0 24 24"
                                          style={{ color: isRowMixActive ? '#ea3663' : '#94a3b8' }}
                                        >
                                          <path d="M20.71 4.63l-1.34-1.34c-.37-.39-1.02-.39-1.41 0L9 12.25 11.75 15l8.96-8.96c.39-.39.39-1.04 0-1.41zM7 14a3 3 0 00-3 3c0 1.31-1.16 2-2 2 .92 1.22 2.49 2 4 2a4 4 0 004-4c0-1.66-1.34-3-3-3z" />
                                        </svg>
                                      )}
                                    </button>
                                  )}
                                  {!displayMatch.is_mix && displayMatch.id && (displayMatch.inventory ?? 0) > 0 && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleMarkAsOut(displayMatch.id);
                                      }}
                                      disabled={markingOut[displayMatch.id]}
                                      className="p-0.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-red-600 hover:text-red-700"
                                      title="Mark as out (set inventory to 0)"
                                    >
                                      {markingOut[displayMatch.id] ? (
                                        <svg className="w-3.5 h-3.5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                        </svg>
                                      ) : (
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                      )}
                                    </button>
                                  )}
                                  {displayMatch.is_mix && displayMatch.color1?.id && displayMatch.color2?.id && 
                                   ((displayMatch.color1.inventory ?? 0) > 0 || (displayMatch.color2.inventory ?? 0) > 0) && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleMarkAsOut([displayMatch.color1.id, displayMatch.color2.id]);
                                      }}
                                      disabled={markingOut[displayMatch.color1.id] || markingOut[displayMatch.color2.id]}
                                      className="p-0.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-red-600 hover:text-red-700"
                                      title="Mark both colors as out (set inventory to 0)"
                                    >
                                      {(markingOut[displayMatch.color1.id] || markingOut[displayMatch.color2.id]) ? (
                                        <svg className="w-3.5 h-3.5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                        </svg>
                                      ) : (
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                      )}
                                    </button>
                                  )}
                                </div>
                              </div>
                              {displayMatch.is_mix ? (
                                // Two-color mix display
                                <div className="space-y-1">
                                  <div className="flex items-center gap-1.5">
                                    <div
                                      className="w-7 h-7 rounded-lg shadow-sm border border-slate-200 flex-shrink-0"
                                      style={{ backgroundColor: displayMatch.mixed_hex }}
                                      title={`Mixed: ${displayMatch.mixed_hex}`}
                                    ></div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs font-medium text-slate-800 truncate">Two-color mix</p>
                                      {displayMatch.color1.color_number && displayMatch.color2.color_number && (
                                        <p className="text-xs text-slate-500 font-mono truncate">{displayMatch.color1.color_number} + {displayMatch.color2.color_number}</p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="pl-4 space-y-0.5">
                                    <div className="flex items-center gap-1">
                                      <div
                                        className="w-5 h-5 rounded shadow-sm border border-slate-200 flex-shrink-0"
                                        style={{ backgroundColor: displayMatch.color1.hex }}
                                      ></div>
                                      <p className="text-xs text-slate-700 truncate" style={{ maxWidth: '100px' }}>
                                        {displayMatch.color1.name}
                                        {displayMatch.color1.color_number && <span className="text-slate-500 ml-1">{displayMatch.color1.color_number}</span>}
                                      </p>
                                      <span className="text-xs text-slate-500 ml-auto">{Math.round((1 - displayMatch.ratio) * 100)}%</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <div
                                        className="w-5 h-5 rounded shadow-sm border border-slate-200 flex-shrink-0"
                                        style={{ backgroundColor: displayMatch.color2.hex }}
                                      ></div>
                                      <p className="text-xs text-slate-700 truncate" style={{ maxWidth: '100px' }}>
                                        {displayMatch.color2.name}
                                        {displayMatch.color2.color_number && <span className="text-slate-500 ml-1">{displayMatch.color2.color_number}</span>}
                                      </p>
                                      <span className="text-xs text-slate-500 ml-auto">{Math.round(displayMatch.ratio * 100)}%</span>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                // Single color match display
                                <div className="flex items-center gap-1.5">
                                  <div
                                    className="w-7 h-7 rounded-lg shadow-sm border border-slate-200 flex-shrink-0"
                                    style={{ backgroundColor: displayMatch.hex }}
                                  ></div>
                                  <div className="flex-1 min-w-0" style={{ maxWidth: '100px' }}>
                                    <p className="text-xs font-medium text-slate-800 truncate">{displayMatch.name}</p>
                                    {displayMatch.color_number && (
                                      <p className="text-xs text-slate-500 font-mono truncate">{displayMatch.color_number}</p>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="text-xs text-slate-400 text-center mt-1.5 pt-1.5 border-t border-slate-100">
                            {displayMatch.delta_e !== undefined ? (
                              <>{deltaEToPercentage(displayMatch.delta_e)}% match</>
                            ) : (
                              <>Distance: {Math.round(displayMatch.distance || 0)}</>
                            )}
                          </div>
                        </div>
                              );
                              })}
                            </div>
                          ))}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full text-center p-4">
                      <div>
                        <p className="text-sm text-slate-400 mb-1">
                          No matches found
                        </p>
                      </div>
                    </div>
                  )}
                  </div>
                </div>
              </div>
            </div>
          )}
              </>
            )}
          </div>
        </div>

        {/* Right Column: Video Selection */}
        <div className={`${isMatchesCollapsed ? 'lg:col-span-1' : 'lg:col-span-3'} bg-white flex flex-col relative`} style={{ minHeight: '600px', height: '100%', maxHeight: '100%' }}>
          
          {!selectedVideo && !selectedImage ? (
            <div className="p-4 space-y-5 h-full flex flex-col">
              <div className="flex-shrink-0 bg-slate-50 border border-slate-200 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-slate-800 mb-1">Load a YouTube video</h3>
                <p className="text-xs text-slate-500 mb-3">Paste a YouTube URL or video ID to start coloring along.</p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="text"
                    value={videoId}
                    onChange={(e) => {
                      setVideoId(e.target.value);
                      if (videoLoadError) {
                        setVideoLoadError(null);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleLoadVideo();
                      }
                    }}
                    placeholder="https://www.youtube.com/watch?v=... or video ID"
                    className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-offset-2"
                    style={{ focusRingColor: '#ea3663' }}
                  />
                  {playlists.length > 0 && (
                    <DropdownMenu
                      className="w-full sm:w-56 flex-shrink-0"
                      options={[
                        { value: '', label: 'No playlist', selectedLabel: 'Add to playlist (optional)' },
                        ...playlists.map(playlist => ({
                          value: String(playlist.id),
                          label: playlist.title || playlist.name || `Playlist ${playlist.id}`,
                        })),
                      ]}
                      value={loadVideoPlaylistId}
                      onChange={setLoadVideoPlaylistId}
                      placeholder="Add to playlist (optional)"
                      searchable={playlists.length > 8}
                      searchPlaceholder="Search playlists..."
                    />
                  )}
                  <button
                    onClick={handleLoadVideo}
                    disabled={!videoId.trim() || loadingVideo}
                    className="px-4 py-2 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed sm:w-auto w-full"
                    style={{
                      backgroundColor: '#ea3663'
                    }}
                    onMouseEnter={(e) => !e.target.disabled && (e.target.style.backgroundColor = '#d12a4f')}
                    onMouseLeave={(e) => !e.target.disabled && (e.target.style.backgroundColor = '#ea3663')}
                  >
                    {loadingVideo ? 'Loading...' : 'Load Video'}
                  </button>
                </div>
                {videoLoadError && (
                  <p className="mt-2 text-sm text-red-600">{videoLoadError}</p>
                )}
              </div>

              <div className="flex-1 min-h-0 flex flex-col">
                <div className="flex-shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-800">Or choose from your library</h3>
                    <p className="text-xs text-slate-500 mt-0.5">Browse recent videos and playlists</p>
                  </div>
                  <div className="relative w-full sm:w-56 flex-shrink-0">
                    <svg className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 100-15 7.5 7.5 0 000 15z" />
                    </svg>
                    <input
                      type="search"
                      value={librarySearch}
                      onChange={(e) => {
                        setLibrarySearch(e.target.value);
                        if (activePlaylist) {
                          setActivePlaylist(null);
                          setPlaylistVideos([]);
                        }
                      }}
                      placeholder="Search library"
                      className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-offset-2"
                      style={{ focusRingColor: '#ea3663' }}
                    />
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto space-y-6">
                <YouTubeImportBanner />
                {activePlaylist ? (
                  <div>
                    <button
                      type="button"
                      onClick={() => {
                        setActivePlaylist(null);
                        setPlaylistVideos([]);
                      }}
                      className="text-sm text-slate-500 hover:text-slate-700 mb-3"
                    >
                      ← Back to library
                    </button>
                    <h3 className="text-sm font-semibold text-slate-800 mb-3">{activePlaylist.title || 'Playlist'}</h3>
                    {loadingPlaylistVideos ? (
                      <p className="text-slate-500 py-8 text-center">Loading playlist videos...</p>
                    ) : filteredPlaylistVideos.length === 0 ? (
                      <p className="text-slate-500 py-8 text-center">No videos in this playlist.</p>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                        {filteredPlaylistVideos.map(renderVideoCard)}
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-slate-800">
                          {debouncedLibrarySearch ? 'Videos' : 'Recent Videos'}
                        </h3>
                        <button
                          type="button"
                          onClick={() => navigate('/studio/inspiration')}
                          className="text-sm font-medium hover:underline"
                          style={{ color: '#ea3663' }}
                        >
                          View all
                        </button>
                      </div>
                      {loadingPickerVideos ? (
                        <p className="text-slate-500 py-8 text-center">Loading videos...</p>
                      ) : pickerVideos.length === 0 && importing ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                          <YouTubeImportPlaceholder count={4} />
                        </div>
                      ) : pickerVideos.length === 0 ? (
                        <p className="text-slate-500 py-6 text-center">
                          {debouncedLibrarySearch ? 'No videos match your search.' : 'No videos yet. Load a YouTube URL or add inspiration to get started.'}
                        </p>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                          {importing && <YouTubeImportPlaceholder />}
                          {pickerVideos.map(renderVideoCard)}
                        </div>
                      )}
                    </div>

                    <div>
                      <h3 className="text-sm font-semibold text-slate-800 mb-3">Playlists</h3>
                      {loadingPlaylists ? (
                        <p className="text-slate-500 py-8 text-center">Loading playlists...</p>
                      ) : filteredPlaylists.length === 0 && importing ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                          <YouTubeImportPlaceholder count={3} />
                        </div>
                      ) : filteredPlaylists.length === 0 ? (
                        <p className="text-slate-500 py-6 text-center">
                          {debouncedLibrarySearch ? 'No playlists match your search.' : 'No playlists yet.'}
                        </p>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                          {importing && <YouTubeImportPlaceholder />}
                          {filteredPlaylists.map((playlist) => {
                            const previewThumb = playlist.thumb
                              || playlist.preview_items?.[0]?.thumb
                              || (playlist.preview_items?.[0]?.embed_id ? `https://img.youtube.com/vi/${playlist.preview_items[0].embed_id}/hqdefault.jpg` : null)
                              || 'https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=400&h=300&fit=crop';
                            const videoCount = playlist.preview_total ?? playlist.videos?.length ?? playlist.preview_items?.length ?? 0;
                            return (
                              <button
                                key={playlist.id}
                                onClick={() => handlePlaylistSelect(playlist)}
                                className="bg-slate-50 rounded-xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-md transition-all text-left p-0 flex flex-col"
                                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#ea3663'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; }}
                              >
                                <div className="relative aspect-video bg-slate-100 overflow-hidden">
                                  <img
                                    src={previewThumb}
                                    alt={playlist.title || 'Playlist'}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      e.target.src = 'https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=400&h=300&fit=crop';
                                    }}
                                  />
                                </div>
                                <div className="p-2">
                                  <p className="text-sm font-medium text-slate-800 line-clamp-2">{playlist.title || 'Untitled Playlist'}</p>
                                  <p className="text-xs text-slate-500 mt-1">
                                    {videoCount} {videoCount === 1 ? 'video' : 'videos'}
                                  </p>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </>
                )}
                </div>
              </div>
            </div>
          ) : selectedImage ? (
            <div className="h-full flex flex-col">
              <div className="flex-1 bg-slate-900 overflow-hidden flex items-center justify-center" style={{ minHeight: '600px' }}>
                <img
                  src={selectedImage.thumbnail || selectedImage.path || 'https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=800&h=600&fit=crop'}
                  alt={selectedImage.title || 'Image'}
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=800&h=600&fit=crop';
                  }}
                />
              </div>
              <div className="flex items-center justify-between p-2 flex-shrink-0 bg-white border-t border-slate-200">
                <div>
                  <h4 className="text-sm font-semibold text-slate-800">{selectedImage.title || 'Image'}</h4>
                  <p className="text-xs text-slate-600">Image ID: {selectedImage.id}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setShowJournalModal(true);
                      setJournalMinimized(false);
                    }}
                    className="px-3 py-1.5 text-sm text-white rounded-lg font-medium transition-colors"
                    style={{
                      backgroundColor: '#ea3663'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#d12a4f'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#ea3663'}
                  >
                    {journalActionLabel}
                  </button>
                  <button
                    onClick={() => {
                      setSelectedImage(null);
                      // Clear query parameter
                      navigate('/color-along', { replace: true });
                    }}
                    className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    Change Image
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full flex flex-col">
              <div className="flex-1 flex flex-col lg:flex-row min-h-0">
              <div className="flex-1 min-w-0 bg-slate-900 overflow-hidden flex items-center justify-center" style={{ minHeight: '600px' }}>
                {youtubeApiLoaded ? (
                  <div id="youtube-player-container" className="youtube-player-frame"></div>
                ) : (
                  (() => {
                    const savedProgress = getVideoProgress(selectedVideo.id);
                    const embedUrl = savedProgress 
                      ? `https://www.youtube.com/embed/${selectedVideo.id}?start=${savedProgress}`
                      : `https://www.youtube.com/embed/${selectedVideo.id}`;
                    return (
                      <div className="youtube-player-frame">
                        <iframe
                          width="100%"
                          height="100%"
                          src={embedUrl}
                          title={selectedVideo.title}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                      </div>
                    );
                  })()
                )}
              </div>
              {showComments && (
                <VideoComments
                  videoId={selectedVideo.id}
                  onSeek={youtubeApiLoaded ? seekVideo : undefined}
                  onClose={toggleComments}
                  locked={areCommentsLocked(user)}
                  className="w-full lg:w-[360px] flex-shrink-0 border-t lg:border-t-0 lg:border-l border-slate-200 h-[480px] lg:h-auto"
                />
              )}
              </div>
              <div className="flex items-center justify-between p-2 flex-shrink-0 bg-white border-t border-slate-200">
                <div>
                  <h4 className="text-sm font-semibold text-slate-800">{selectedVideo.title}</h4>
                  <p className="text-xs text-slate-600">Video ID: {selectedVideo.id}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={toggleComments}
                    aria-pressed={showComments}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                      showComments ? 'bg-slate-800 text-white' : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    Comments
                  </button>
                  <button
                    onClick={() => {
                      setShowJournalModal(true);
                      setJournalMinimized(false);
                    }}
                    className="px-3 py-1.5 text-sm text-white rounded-lg font-medium transition-colors"
                    style={{
                      backgroundColor: '#ea3663'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#d12a4f'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#ea3663'}
                  >
                    {journalActionLabel}
                  </button>
                  <button
                    onClick={() => {
                      setSelectedVideo(null);
                      setVideoId('');
                      // Clear query parameter
                      navigate('/color-along', { replace: true });
                    }}
                    className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    Change Video
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div 
          className="fixed top-4 right-4 z-50 bg-green-500 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 animate-fade-in"
          style={{
            animation: 'fadeIn 0.3s ease-out'
          }}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span>{successMessage}</span>
        </div>
      )}

      {/* Journal Entry Modal - Anchored to bottom right of video area */}
      {showJournalModal && (
        <>
          <style>{`
            @keyframes slideUpRight {
              from {
                transform: translateY(100%);
                opacity: 0;
              }
              to {
                transform: translateY(0);
                opacity: 1;
              }
            }
            @keyframes fadeIn {
              from {
                opacity: 0;
                transform: translateY(-10px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            @media (min-width: 1024px) {
              .journal-modal-container {
                width: calc(75% - 0.5rem);
                max-width: 600px;
                right: 0;
              }
            }
            @media (max-width: 1023px) {
              .journal-modal-container {
                width: 100%;
                max-width: 100%;
                right: 0;
              }
            }
          `}</style>
          <div 
            className="fixed bottom-0 z-50 journal-modal-container"
            style={{
              animation: 'slideUpRight 0.3s ease-out'
            }}
          >
            <div 
              className={`bg-white rounded-t-2xl shadow-2xl w-full border-t border-l border-r border-slate-200 ${
                journalMinimized ? '' : 'max-h-[85vh] overflow-y-auto'
              }`}
            >
            
            {/* Modal Header */}
            <div
              className={`sticky top-0 bg-white p-3 flex items-center justify-between z-10 rounded-t-2xl ${
                journalMinimized ? 'cursor-pointer hover:bg-slate-50' : 'border-b border-slate-200'
              }`}
              onClick={journalMinimized ? () => setJournalMinimized(false) : undefined}
            >
              <div className="flex items-center gap-2 min-w-0">
                <h2 className="text-base font-semibold text-slate-800 font-venti">
                  {existingJournalEntry ? 'Continue Coloring Log' : 'Add Journal Entry'}
                </h2>
                {journalMinimized && (
                  <span className="text-xs text-slate-500 truncate">Draft saved here · click to continue</span>
                )}
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setJournalMinimized((prev) => !prev);
                  }}
                  className="p-1 text-slate-400 hover:text-slate-600 transition-colors"
                  title={journalMinimized ? 'Expand' : 'Minimize'}
                  aria-label={journalMinimized ? 'Expand journal entry' : 'Minimize journal entry'}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {journalMinimized ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    )}
                  </svg>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    discardJournalDraft();
                  }}
                  className="p-1 text-slate-400 hover:text-slate-600 transition-colors"
                  title="Close without saving"
                  aria-label="Close journal entry"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Body - hidden rather than unmounted when minimized so the editor keeps its state */}
            <div className={`p-3 space-y-3 ${journalMinimized ? 'hidden' : ''}`}>
              {loadingJournalData ? (
                <div className="flex items-center justify-center py-8">
                  <p className="text-slate-500">Loading form data...</p>
                </div>
              ) : (
                <>
                  {existingJournalEntry && (
                    <p className="text-xs text-slate-500">
                      Project dates:{' '}
                      {formatEntryProjectDates(existingJournalEntry)}
                    </p>
                  )}

                  {/* Date */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      Session Date
                    </label>
                    <input
                      type="date"
                      value={journalFormData.date}
                      onChange={(e) => setJournalFormData({ ...journalFormData, date: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-800 focus:outline-none focus:ring-2 focus:ring-offset-2"
                      style={{ focusRingColor: '#ea3663' }}
                    />
                  </div>

                  {/* Inspiration */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      Inspiration
                    </label>
                    <InspirationDropdown
                      value={journalFormData.inspiration}
                      onChange={(value) => setJournalFormData({ ...journalFormData, inspiration: value })}
                      placeholder="Select inspiration..."
                    />
                  </div>

                  {/* Video Pencil Set (brand -> set, same flow as Color Conversion) */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      Video Pencil Set
                    </label>
                    <div className="bg-slate-50 shadow-sm border border-slate-200 p-2 rounded-lg">
                      {journalFormData.videoPencilSet && journalVideoSelectedSet ? (
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-800 truncate">
                              {journalVideoSelectedSet.name || 'Unknown'}
                            </p>
                            <p className="text-xs text-slate-600 truncate">
                              {typeof journalVideoSelectedSet.brand === 'object'
                                ? (journalVideoSelectedSet.brand?.name || 'Unknown')
                                : (journalVideoSelectedSet.brand || 'Unknown')}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setJournalFormData({ ...journalFormData, videoPencilSet: '' });
                              setJournalVideoSelectedSet(null);
                              setJournalVideoStep('brand');
                              setJournalVideoSelectedBrand(null);
                              setJournalVideoSetsForBrand([]);
                            }}
                            className="text-xs text-slate-500 hover:text-slate-700 underline flex-shrink-0"
                          >
                            Change
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {journalVideoStep !== 'brand' && (
                            <button
                              type="button"
                              onClick={handleJournalVideoBack}
                              className="flex items-center space-x-1 text-xs text-slate-600 hover:text-slate-800 transition-colors"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                              </svg>
                              <span>Back</span>
                            </button>
                          )}

                          <div className="text-xs font-medium text-slate-700 mb-1">
                            {journalVideoStep === 'brand' && 'Select a Brand'}
                            {journalVideoStep === 'set' && `Select a Set for ${journalVideoSelectedBrand?.name || ''}`}
                          </div>

                          {journalVideoStep === 'brand' && (
                            <div className="border border-slate-200 rounded p-1.5 max-h-36 overflow-y-auto bg-white">
                              {loadingBrands ? (
                                <div className="text-center py-2 text-xs text-slate-500">Loading brands...</div>
                              ) : brands.length === 0 ? (
                                <div className="text-center py-2 text-xs text-slate-500">No brands available</div>
                              ) : (
                                <div className="space-y-1">
                                  {brands.map((brand) => (
                                    <button
                                      key={`journal-video-brand-${brand.id}`}
                                      type="button"
                                      onClick={() => handleJournalVideoBrandSelect(brand)}
                                      className="w-full flex items-center gap-2 p-1.5 rounded border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-colors text-left"
                                    >
                                      <ListThumb src={brand.thumbnail} alt={brand.name} icon="brand" />
                                      <span className="flex-1 min-w-0 text-xs font-medium text-slate-800 truncate">{brand.name}</span>
                                      <svg className="w-3 h-3 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                      </svg>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                          {journalVideoStep === 'set' && journalVideoSelectedBrand && (
                            <div className="border border-slate-200 rounded p-1.5 max-h-36 overflow-y-auto bg-white">
                              {loadingJournalVideoSets ? (
                                <div className="text-center py-2 text-xs text-slate-500">Loading sets...</div>
                              ) : journalVideoSetsForBrand.length === 0 ? (
                                <div className="text-center py-2 text-xs text-slate-500">No sets available for this brand</div>
                              ) : (
                                <div className="space-y-1">
                                  {journalVideoSetsForBrand.map((set) => (
                                    <button
                                      key={`journal-video-set-${set.id}`}
                                      type="button"
                                      onClick={() => handleJournalVideoSetSelect(set)}
                                      className="w-full flex items-center gap-2 p-1.5 rounded border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-colors text-left"
                                    >
                                      <ListThumb src={set.thumb} alt={set.name || 'Set'} icon="set" />
                                      <span className="flex-1 min-w-0 text-xs font-medium text-slate-800 truncate">{set.name || 'Unknown'}</span>
                                      <svg className="w-3 h-3 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                      </svg>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* User Pencil Set (selected state matches Video Pencil Set) */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      Your Pencil Set
                    </label>
                    <div className="bg-slate-50 shadow-sm border border-slate-200 p-2 rounded-lg">
                      {journalFormData.userPencilSet && journalUserSelectedSet ? (
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-800 truncate">
                              {journalUserSelectedSet.name || 'Unknown'}
                            </p>
                            <p className="text-xs text-slate-600 truncate">
                              {typeof journalUserSelectedSet.brand === 'object'
                                ? (journalUserSelectedSet.brand?.name || 'Unknown')
                                : (journalUserSelectedSet.brand || 'Unknown')}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setJournalFormData({ ...journalFormData, userPencilSet: '' });
                            }}
                            className="text-xs text-slate-500 hover:text-slate-700 underline flex-shrink-0"
                          >
                            Change
                          </button>
                        </div>
                      ) : (
                        <DropdownMenu
                          options={userSetJournalOptions}
                          value={journalFormData.userPencilSet}
                          onChange={(value) => setJournalFormData({ ...journalFormData, userPencilSet: value })}
                          placeholder="Select your pencil set..."
                        />
                      )}
                    </div>
                  </div>

                  {/* Book */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      Book
                    </label>
                    <BookDropdown
                      value={journalFormData.book}
                      onChange={(value) => setJournalFormData({ ...journalFormData, book: value, bookPage: EMPTY_BOOK_PAGE })}
                      placeholder="Select a book (optional)..."
                    />
                    <BookPageFields
                      bookId={journalFormData.book}
                      value={journalFormData.bookPage}
                      onChange={(bookPage) => setJournalFormData((prev) => ({ ...prev, bookPage }))}
                      disabled={savingJournal}
                    />
                  </div>

                  {/* Color Palette */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      Color Palette
                    </label>
                    <DropdownMenu
                      options={palettes.map(palette => ({
                        value: palette.id.toString(),
                        label: palette.name || palette.title || `Palette ${palette.id}`
                      }))}
                      value={journalFormData.palette}
                      onChange={(value) => setJournalFormData({ ...journalFormData, palette: value })}
                      placeholder="Select a color palette (optional)..."
                    />
                  </div>

                  {/* Color Combos (Multi-select) */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      Color Combos
                    </label>
                    <MultiSelectDropdown
                      options={combos.map(combo => ({
                        value: combo.id.toString(),
                        label: combo.name || combo.title || `Combo ${combo.id}`
                      }))}
                      value={journalFormData.combos}
                      onChange={(value) => setJournalFormData({ ...journalFormData, combos: value })}
                      placeholder="Select color combos (optional)..."
                      searchPlaceholder="Search combos..."
                      emptyMessage="No color combos available"
                    />
                  </div>

                  {/* Tags */}
                  <div>
                    <TagSelect
                      value={journalFormData.tags || []}
                      onChange={(tags) => setJournalFormData({ ...journalFormData, tags })}
                      disabled={savingJournal}
                    />
                  </div>

                  {existingJournalEntry?.session_notes?.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-slate-700">Earlier notes</p>
                      <div className="max-h-36 overflow-y-auto space-y-2 border border-slate-200 rounded-lg p-2 bg-slate-50">
                        {existingJournalEntry.session_notes.map((note) => {
                          const videoRange = formatVideoTimeRange(note.video_start_seconds, note.video_end_seconds);
                          return (
                            <div key={note.id} className="text-xs text-slate-600">
                              <div className="font-medium text-slate-700">
                                {formatSessionNoteStamp(note)}{videoRange ? ` · ${videoRange}` : ''}
                              </div>
                              {note.notes ? (
                                <div
                                  className="text-slate-600 [&_p]:m-0"
                                  dangerouslySetInnerHTML={{ __html: sanitizeRichTextHtml(note.notes) }}
                                />
                              ) : (
                                <p className="text-slate-400 italic">No written notes</p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      Notes
                    </label>
                    <RichTextEditor
                      value={journalFormData.notes}
                      onChange={(value) => setJournalFormData({ ...journalFormData, notes: value })}
                      placeholder=""
                    />
                  </div>
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div className={`sticky bottom-0 bg-white border-t border-slate-200 p-3 flex items-center justify-end gap-2 ${journalMinimized ? 'hidden' : ''}`}>
              <button
                onClick={async () => {
                  try {
                    setSavingJournal(true);
                    
                    // Prepare data for API
                    const notesValue = isRichTextEmpty(journalFormData.notes) ? null : journalFormData.notes;
                    const videoStart = journalVideoStartSeconds ?? getYouTubeTimeSeconds();
                    const videoEnd = getYouTubeTimeSeconds();
                    const wasExistingLog = Boolean(existingJournalEntry);
                    const entryData = {
                      date: journalFormData.date,
                      inspiration: parseInspirationValue(journalFormData.inspiration).id,
                      videoPencilSet: journalFormData.videoPencilSet || null,
                      userPencilSet: journalFormData.userPencilSet || null,
                      // Keep legacy field for backward compatibility.
                      pencilSet: journalFormData.userPencilSet || journalFormData.videoPencilSet || null,
                      book: journalFormData.book || null,
                      palette: journalFormData.palette || null,
                      combos: journalFormData.combos.map(id => parseInt(id)),
                      tag_ids: (journalFormData.tags || []).filter(t => t.id).map(t => t.id),
                      tag_names: (journalFormData.tags || []).filter(t => !t.id).map(t => (t.icon ? { tag: t.tag, icon: t.icon } : t.tag)),
                      notes: notesValue,
                      ...(videoStart !== null ? { video_start_seconds: videoStart } : {}),
                      ...(videoEnd !== null ? { video_end_seconds: videoEnd } : {}),
                      ...(journalFormData.book ? bookPagePayload(journalFormData.bookPage) : {})
                    };

                    // Remove null/empty string values (but keep empty arrays for combos)
                    Object.keys(entryData).forEach(key => {
                      if (key !== 'combos' && (entryData[key] === null || entryData[key] === '')) {
                        delete entryData[key];
                      }
                    });

                    const savedEntry = await journalEntriesAPI.create(entryData);
                    setExistingJournalEntry(savedEntry);
                    
                    discardJournalDraft();
                    
                    // Show success message
                    setSuccessMessage([
                      wasExistingLog ? 'Added to your coloring log.' : 'Started this coloring log.',
                      describeSavedBookPage(savedEntry?.book_page)
                    ].filter(Boolean).join(' '));
                    
                    // Auto-dismiss success message after 3 seconds
                    setTimeout(() => {
                      setSuccessMessage('');
                    }, 3000);
                    
                    // Reset form
                    setJournalFormData({
                      date: getLocalDateString(),
                      inspiration: '',
                      videoPencilSet: '',
                      userPencilSet: '',
                      book: '',
                      bookPage: EMPTY_BOOK_PAGE,
                      palette: '',
                      combos: [],
                      tags: [],
                      notes: ''
                    });
                  } catch (error) {
                    console.error('Error saving journal entry:', error);
                    const pageError = error.data?.errors?.book_page_id?.[0] || error.data?.errors?.book_page_number?.[0];
                    alert(pageError || 'Failed to save journal entry. Please try again.');
                  } finally {
                    setSavingJournal(false);
                  }
                }}
                disabled={loadingJournalData || savingJournal}
                className="px-4 py-2 text-sm text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: '#ea3663'
                }}
                onMouseEnter={(e) => !e.target.disabled && (e.target.style.backgroundColor = '#d12a4f')}
                onMouseLeave={(e) => !e.target.disabled && (e.target.style.backgroundColor = '#ea3663')}
              >
                {savingJournal ? 'Saving...' : 'Save Entry'}
              </button>
            </div>
          </div>
        </div>
        </>
      )}
    </div>
  );
};

export default ColorAlong;

